import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  LineStyle,
  CrosshairMode,
  ColorType,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type CandlestickData,
  type HistogramData,
  type LineData,
  type UTCTimestamp,
  type MouseEventParams,
  type LogicalRange,
  type SeriesMarker,
} from 'lightweight-charts';
import { rangeTimeEndingSoon, timeframeSeconds, type Candle } from '@termix/shared';
import {
  autoLevels,
  autoTrends,
  emaSeries,
  orderBlockBreakers,
  orderBlockHints,
  rsiSeries,
  smaSeries,
  volumePoints,
} from '../lib/indicators';
import type { ChartPayload, Drawing } from '../lib/drawings';
import { newId } from '../lib/drawings';
import { fmtPrice, fmtVol, minMoveFor, priceDigits } from '../lib/priceFormat';
import type { BtMarker } from '../lib/backtest';
import { useI18n } from '../lib/i18n';
import { useTheme } from '../lib/theme';

const cssVar = (name: string, fallback: string) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
};

const chartThemeColors = (themeId?: string) => {
  const isIvory = themeId === 'ivory'
    || document.documentElement.getAttribute('data-theme') === 'ivory';
  // Prefer live CSS vars (set with data-theme); fall back if read too early.
  const bg = cssVar('--chart-bg', isIvory ? '#f7f3ea' : '#0e1218');
  const muted = cssVar('--muted', isIvory ? '#6b5f4f' : '#8b97a8');
  const line = cssVar('--line', isIvory ? '#d5cab6' : '#2a3340');
  const text = cssVar('--text', isIvory ? '#2a241c' : '#e8edf5');
  return {
    bg,
    text: muted,
    grid: isIvory ? 'rgba(213,202,182,0.55)' : 'rgba(42,51,64,0.55)',
    border: line,
    cross: isIvory ? 'rgba(42,36,28,0.35)' : 'rgba(232,237,245,0.45)',
    labelBg: line,
    hair: isIvory ? 'rgba(42,36,28,0.18)' : 'rgba(232,237,245,0.22)',
    overlayBg: isIvory ? 'rgba(247,243,234,0.94)' : 'rgba(14,18,24,0.88)',
    textStrong: text,
    /** Default pen for new drawings — light stroke vanishes on ivory. */
    drawDefault: isIvory ? '#2a241c' : '#e8edf5',
  };
};

/** Indicator keys the eraser can disable by clicking the chart. */
type EraseIndKey = 'sma' | 'ema' | 'rsi' | 'volume' | 'autoLevels' | 'autoTrends' | 'orderBlocks' | 'obBreakers';

const FUTURE_BARS = 64;
/** Default bar pixel width when pinning to live (TradingView-ish density). */
const LIVE_BAR_SPACING = 6;
/** Fraction of the visible window reserved as empty future (to the right of last price). */
const FUTURE_VIEW_FRAC = 0.10;
/** Eraser hit square half-size in px — visible box around the cursor. */
const ERASER_HALF = 16;

export type DrawTool = 'pointer' | 'hline' | 'range' | 'trend' | 'ruler' | 'eraser';

/**
 * Pin time scale to the last REAL candle with ~10% of the pane open into the future.
 * Uses time-based setVisibleRange (not logical indices) so overlay LineSeries that
 * accidentally inject timestamps cannot yank the viewport left toward the range start.
 */
/** Set while we programmatically move the viewport — user pans must not fight this. */
let viewportProgrammatic = 0;
const beginProgrammaticViewport = () => {
  viewportProgrammatic += 1;
  window.setTimeout(() => { viewportProgrammatic = Math.max(0, viewportProgrammatic - 1); }, 80);
};

const scrollChartToLatest = (
  chart: IChartApi,
  candles: Candle[],
  hostWidth?: number,
) => {
  if (candles.length < 2) return;
  const last = candles[candles.length - 1];
  const prev = candles[candles.length - 2];
  const step = Math.max(60, Number(last.time) - Number(prev.time));
  const width = Math.max(280, hostWidth || 640);
  const visibleBars = Math.max(40, Math.floor(width / LIVE_BAR_SPACING));
  const futureBars = Math.min(
    FUTURE_BARS,
    Math.max(12, Math.round(visibleBars * FUTURE_VIEW_FRAC)),
  );
  const historyBars = Math.max(24, visibleBars - futureBars);
  const fromIdx = Math.max(0, candles.length - historyBars);
  const fromTime = Number(candles[fromIdx].time);
  const toTime = Number(last.time) + futureBars * step;
  beginProgrammaticViewport();
  try {
    chart.timeScale().applyOptions({
      // Future pad comes from whitespace bars + visible `to`, not rightOffset
      // (rightOffset after injected overlay times was unreliable with a range on).
      rightOffset: 0,
      barSpacing: LIVE_BAR_SPACING,
      minBarSpacing: 2,
    });
    chart.timeScale().setVisibleRange({
      from: fromTime as UTCTimestamp,
      to: toTime as UTCTimestamp,
    });
  } catch {
    try {
      chart.timeScale().setVisibleLogicalRange({
        from: fromIdx,
        to: candles.length - 1 + futureBars,
      });
    } catch { /* ignore */ }
  }
};

type TradeRangeBox = {
  priceLow: number;
  priceHigh: number;
  timeFrom?: number | null;
  timeTo?: number | null;
};

type PayloadOpts = { history?: boolean };

type Props = {
  candles: Candle[];
  /** Reset fit only when this key changes (symbol:tf). */
  seriesKey: string;
  /** Must equal seriesKey before candles are painted — prevents painting 30m under 15m. */
  candlesKey?: string;
  range?: TradeRangeBox | null;
  payload: ChartPayload;
  tool: DrawTool;
  drawColor?: string;
  backtestMarkers?: BtMarker[];
  /** Persisted real WEEX fills — always shown, distinct from range backtest sketch. */
  liveMarkers?: BtMarker[];
  /** Short dashes at fill candles: entry / add (добор) / exit (~1.3× candle wide). */
  levelDashes?: Array<{ time: number; price: number; title: string; color: string; kind?: string }>;
  /** Sketch fill ticks — gated by sketchDashKey so live polls do not rebuild series. */
  sketchDashes?: Array<{ time: number; price: number; title: string; color: string; kind?: string }>;
  sketchDashKey?: string;
  /** Open-position average entry — price line + last-bar tick; clickable when onAvgEntryClick set. */
  avgEntryLevel?: {
    price: number;
    title?: string;
    color?: string;
    selected?: boolean;
    side?: 'long' | 'short';
  } | null;
  /** Unix sec (or ms) — vertical marker when the live strategy was armed. */
  strategyStartTime?: number | null;
  /** When true, new range gets timeFrom/timeTo; when false — price band only. */
  rangeUseTime?: boolean;
  /** Candle timeframe — used for “time ending soon” warn window. */
  timeframe?: string;
  strategyName?: string;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Pointer-tool click near avg entry (or badge) selects the open position. */
  onAvgEntryClick?: () => void;
  onPayloadChange: (next: ChartPayload, opts?: PayloadOpts) => void;
  onRangePick?: (range: TradeRangeBox) => void;
  onRangeClear?: () => void;
  live?: boolean;
  /** Fired when the user pans near the left edge — DeskPage may deep-load older bars. */
  onNeedOlderHistory?: () => void;
  historyLoading?: boolean;
  /** True after a history page returned nothing new (stop requesting). */
  historyExhausted?: boolean;
  /** Live L2 book — widens order-block price bands and fills volume labels. */
  depth?: { bids: Array<[number, number]>; asks: Array<[number, number]> } | null;
};

const fmtCountdown = (sec: number) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const RANGE_DRAW_ID = '__strategy_range__';

type OhlcInfo = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  source: 'hover' | 'last';
};

const normTime = (t: number) => {
  let x = Number(t);
  if (!Number.isFinite(x)) return NaN;
  if (x > 1e12) x = Math.floor(x / 1000);
  return Math.floor(x);
};

/** WEEX klines arrive mostly newest-first; LWC setData requires unique ascending times. */
const normalizeCandles = (candles: Candle[]): Candle[] => {
  const byTime = new Map<number, Candle>();
  for (const c of candles) {
    const time = normTime(c.time);
    const open = Number(c.open);
    const high = Number(c.high);
    const low = Number(c.low);
    const close = Number(c.close);
    // Reject zero/negative OHLC — a single open=0 bar collapses the whole price scale.
    if (!Number.isFinite(time) || ![open, high, low, close].every((x) => Number.isFinite(x) && x > 0)) continue;
    byTime.set(time, {
      time,
      open, high, low, close,
      volume: Number(c.volume) || 0,
    });
  }
  return [...byTime.values()].sort((a, b) => a.time - b.time);
};

const toCandlePoint = (c: Candle): CandlestickData => ({
  time: c.time as UTCTimestamp,
  open: c.open,
  high: c.high,
  low: c.low,
  close: c.close,
});

const toVolPoint = (c: Candle): HistogramData => ({
  time: c.time as UTCTimestamp,
  value: c.volume || 0,
  color: c.close >= c.open ? 'rgba(61,186,122,0.45)' : 'rgba(226,91,91,0.45)',
});

/** Whitespace bars past the last candle so the time axis graduates into the future (clickable). */
const withFutureWhitespace = (sorted: Candle[], tfHint: string): Array<CandlestickData | { time: UTCTimestamp }> => {
  const data: Array<CandlestickData | { time: UTCTimestamp }> = sorted.map(toCandlePoint);
  if (sorted.length < 2) return data;
  const step = Math.max(
    timeframeSeconds(tfHint),
    Math.max(60, sorted[sorted.length - 1].time - sorted[sorted.length - 2].time),
  );
  let t = sorted[sorted.length - 1].time;
  for (let i = 0; i < FUTURE_BARS; i++) {
    t += step;
    data.push({ time: t as UTCTimestamp });
  }
  return data;
};

export default function ChartPane({
  candles, seriesKey, candlesKey, range, payload, tool, drawColor = '#e8edf5',
  backtestMarkers = [], liveMarkers = [], levelDashes = [], sketchDashes = [],
  sketchDashKey = '', avgEntryLevel = null,
  strategyStartTime = null,
  rangeUseTime = true, timeframe, strategyName,
  selectedId = null, onSelect, onAvgEntryClick,
  onPayloadChange, onRangePick, onRangeClear, live,
  onNeedOlderHistory, historyLoading = false, historyExhausted = false,
  depth = null,
}: Props) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const smaRef = useRef<ISeriesApi<'Line'> | null>(null);
  const emaRef = useRef<ISeriesApi<'Line'> | null>(null);
  const rsiRef = useRef<ISeriesApi<'Line'> | null>(null);
  const trendSeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const hintTrendRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const fillDashSeriesRef = useRef<ISeriesApi<'Line'>[]>([]);
  const markersApiRef = useRef<ISeriesMarkersPluginApi<any> | null>(null);
  /** LWC wipes series markers on setData — call after every full candle rewrite. */
  const reapplyMarkersRef = useRef<(() => void) | null>(null);
  const priceLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const hintPriceLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const hintLevelPricesRef = useRef<number[]>([]);
  const rsiZoneLinesRef = useRef<ReturnType<ISeriesApi<'Line'>['createPriceLine']>[]>([]);
  const rsiPaneIndexRef = useRef(0);
  const volPaneIndexRef = useRef(0);
  const draftRef = useRef<{ kind: DrawTool; a?: { time: number; price: number } } | null>(null);
  const candlesRef = useRef(candles);
  const payloadRef = useRef(payload);
  const fittedKeyRef = useRef<string>('');
  const logicalRangeRef = useRef<LogicalRange | null>(null);
  const userInteractedRef = useRef(false);
  /** After tab/symbol load, keep forcing realtime-right until the user pans/zooms. */
  const pinRightRef = useRef(true);
  /** Show TradingView-style «jump to live» when the user has panned away. */
  const [showJumpLive, setShowJumpLive] = useState(false);
  const lastBarTimeRef = useRef<number | null>(null);
  const firstBarTimeRef = useRef<number | null>(null);
  const barCountRef = useRef(0);
  const historyAskAtRef = useRef(0);
  const onNeedOlderHistoryRef = useRef(onNeedOlderHistory);
  onNeedOlderHistoryRef.current = onNeedOlderHistory;
  const historyLoadingRef = useRef(historyLoading);
  historyLoadingRef.current = historyLoading;
  const historyExhaustedRef = useRef(historyExhausted);
  historyExhaustedRef.current = historyExhausted;
  const hostWidth = () => Math.max(280, hostRef.current?.clientWidth || wrapRef.current?.clientWidth || 640);
  const digitsRef = useRef(4);
  const dragRef = useRef<{ kind: 'trend' | 'range' | 'hline'; id: string; end: string; origin?: TradeRangeBox } | null>(null);
  const dragDirtyRef = useRef(false);
  /** Live preview while dragging range — commit to parent only on pointerup. */
  const rangeDraftRef = useRef<TradeRangeBox | null>(null);
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const [ohlc, setOhlc] = useState<OhlcInfo | null>(null);
  const [tick, setTick] = useState(0);
  const [boxCss, setBoxCss] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [obBoxes, setObBoxes] = useState<Array<{
    left: number; top: number; width: number; height: number; side: 'bull' | 'bear';
    /** LuxAlgo-style metrics at the right edge, e.g. "24.1K  41%". */
    metric: string;
    title: string;
    status: 'fresh' | 'tested' | 'mitigated';
    strength: number; midY?: number;
    buyPct?: number; sellPct?: number;
    /** Absolute Y for the metrics chip (avoids stacking inside overlapping boxes). */
    metricTop: number;
    /** Live OB vs breaker (flipped mitigated). */
    kind: 'ob' | 'breaker';
    /** Kept for Safari pan reproject — avoid rebuilding titles every frame. */
    priceLow: number; priceHigh: number; midPrice?: number; tFrom: number; tTo: number;
  }>>([]);
  const obBoxesRef = useRef(obBoxes);
  obBoxesRef.current = obBoxes;
  /** Zone box + metric chip DOM nodes — pan updates styles without React setState lag. */
  const obDomRef = useRef<Map<string, { box: HTMLDivElement; metric: HTMLDivElement | null }>>(new Map());
  /** Recompute OB / breaker zones only when candles/settings/book change — pan just reprojects. */
  const obZonesCacheRef = useRef<{
    key: string;
    zones: ReturnType<typeof orderBlockHints>;
    breakers: ReturnType<typeof orderBlockBreakers>;
  } | null>(null);
  const [eraseIndHit, setEraseIndHit] = useState<EraseIndKey | null>(null);
  const [paneChips, setPaneChips] = useState<Array<{
    key: 'rsi' | 'volume'; label: string; left: number; top: number; mode: 'main' | 'separate';
  }>>([]);
  const paneChipsRef = useRef(paneChips);
  paneChipsRef.current = paneChips;
  const [dragChip, setDragChip] = useState<{ key: 'rsi' | 'volume'; x: number; y: number } | null>(null);
  const [draftPt, setDraftPt] = useState<{ x: number; y: number } | null>(null);
  const [handles, setHandles] = useState<Array<{ id: string; end: string; x: number; y: number; color: string; kind: 'trend' | 'range' | 'hline' }>>([]);
  const [eraseHit, setEraseHit] = useState<string | null>(null);
  const [cross, setCross] = useState<{ x: number; y: number } | null>(null);
  const [barLeftSec, setBarLeftSec] = useState<number | null>(null);
  const [priceLabelY, setPriceLabelY] = useState<number | null>(null);
  const [avgEntryY, setAvgEntryY] = useState<number | null>(null);
  const [strategyStartX, setStrategyStartX] = useState<number | null>(null);
  /** Safari: React setState during pan stalls WebKit + LWC canvas for seconds — poke DOM instead. */
  const rangeBoxElRef = useRef<HTMLDivElement | null>(null);
  const avgEntryElRef = useRef<HTMLButtonElement | null>(null);
  const strategyStartElRef = useRef<HTMLDivElement | null>(null);
  const barCdElRef = useRef<HTMLDivElement | null>(null);
  const handlesElRef = useRef<Map<string, HTMLElement>>(new Map());
  const boxCssRef = useRef(boxCss);
  boxCssRef.current = boxCss;
  const handlesRef = useRef(handles);
  handlesRef.current = handles;
  const avgEntryYRef = useRef(avgEntryY);
  avgEntryYRef.current = avgEntryY;
  const strategyStartXRef = useRef(strategyStartX);
  strategyStartXRef.current = strategyStartX;
  const priceLabelYRef = useRef(priceLabelY);
  priceLabelYRef.current = priceLabelY;
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  /** Ephemeral TradingView-style measure (not saved to drawings). */
  const [ruler, setRuler] = useState<{
    t1: number; p1: number; t2: number; p2: number; dragging: boolean;
  } | null>(null);
  const rulerRef = useRef(ruler);
  rulerRef.current = ruler;
  const avgEntryRef = useRef(avgEntryLevel);
  avgEntryRef.current = avgEntryLevel;
  const onAvgEntryClickRef = useRef(onAvgEntryClick);
  onAvgEntryClickRef.current = onAvgEntryClick;
  const strategyStartRef = useRef(strategyStartTime);
  strategyStartRef.current = strategyStartTime;

  candlesRef.current = candles;
  payloadRef.current = payload;

  const tfFromSeriesKey = () => {
    // seriesKey is `market:SYMBOL:tf` (Spot+) or legacy `SYMBOL:tf`.
    const parts = seriesKey.split(':');
    return parts[parts.length - 1] || '15m';
  };
  const tfSec = timeframeSeconds(timeframe || tfFromSeriesKey());
  const timeWarn = rangeTimeEndingSoon(range || null, nowSec, tfSec);
  const timeWarnTitle = timeWarn.soon
    ? (Number.isFinite(timeWarn.leftSec) && timeWarn.leftSec < 1e8
      ? t.rangeTimeEndingLeft(fmtCountdown(timeWarn.leftSec))
      : t.rangeTimeEnding)
    : undefined;

  useEffect(() => {
    if (!range?.timeTo) return;
    const id = window.setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [range?.timeTo]);

  // Bar-close countdown glued under the last-price pill (same color as candle direction)
  useEffect(() => {
    if (payload.indicators?.barCountdown === false) {
      setBarLeftSec(null);
      return;
    }
    const syncPriceY = () => {
      const series = candleRef.current;
      const sorted = normalizeCandles(candlesRef.current);
      if (!series || !sorted.length) { setPriceLabelY(null); return; }
      const py = series.priceToCoordinate(sorted[sorted.length - 1].close);
      setPriceLabelY(py != null ? Number(py) : null);
    };
    const tickClock = () => {
      const sorted = normalizeCandles(candlesRef.current);
      if (!sorted.length) { setBarLeftSec(null); return; }
      const last = sorted[sorted.length - 1];
      const step = Math.max(
        timeframeSeconds(tfFromSeriesKey()),
        sorted.length >= 2 ? Math.max(60, last.time - sorted[sorted.length - 2].time) : 60,
      );
      const closeAt = last.time + step;
      const left = closeAt - Math.floor(Date.now() / 1000);
      setBarLeftSec(left);
      syncPriceY();
    };
    tickClock();
    const id = window.setInterval(tickClock, 250);
    const chart = chartRef.current;
    const onRange = () => syncPriceY();
    try { chart?.timeScale().subscribeVisibleLogicalRangeChange(onRange); } catch { /* ignore */ }
    window.addEventListener('resize', syncPriceY);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', syncPriceY);
      try { chart?.timeScale().unsubscribeVisibleLogicalRangeChange(onRange); } catch { /* ignore */ }
    };
  }, [seriesKey, candles.length, payload.indicators?.barCountdown]);

  /** Map unix sec → x; extrapolate past last bar so future range edges stay visible. */
  const timeToX = (t: number): number | null => {
    const chart = chartRef.current;
    if (!chart) return null;
    const direct = chart.timeScale().timeToCoordinate(Math.floor(t) as UTCTimestamp);
    if (direct != null) return direct;
    const sorted = normalizeCandles(candlesRef.current);
    if (sorted.length < 2) return null;
    const a = sorted[sorted.length - 2];
    const b = sorted[sorted.length - 1];
    const xa = chart.timeScale().timeToCoordinate(a.time as UTCTimestamp);
    const xb = chart.timeScale().timeToCoordinate(b.time as UTCTimestamp);
    if (xa == null || xb == null || b.time === a.time) return null;
    return xb + ((t - b.time) * (xb - xa)) / (b.time - a.time);
  };

  /** Map x → unix sec (incl. future whitespace / extrapolated zone). */
  const xToTime = (x: number): number | null => {
    const chart = chartRef.current;
    if (!chart) return null;
    const direct = chart.timeScale().coordinateToTime(x);
    if (direct != null) return Number(direct);
    const sorted = normalizeCandles(candlesRef.current);
    if (sorted.length < 2) return null;
    const a = sorted[sorted.length - 2];
    const b = sorted[sorted.length - 1];
    const xa = chart.timeScale().timeToCoordinate(a.time as UTCTimestamp);
    const xb = chart.timeScale().timeToCoordinate(b.time as UTCTimestamp);
    if (xa == null || xb == null || xb === xa) return null;
    return b.time + ((x - xb) * (b.time - a.time)) / (xb - xa);
  };

  const chartReady = () => (
    fittedKeyRef.current === seriesKey
    && normalizeCandles(candlesRef.current).length >= 20
  );

  /**
   * Single geometry for the strategy range box + grips.
   * Clamped into the plot (leave room for the right price scale) so handles
   * never float past the visible gold box when timeTo is future / off-scale.
   */
  const activeRange = (): TradeRangeBox | null => rangeDraftRef.current || rangeRef.current || null;

  const rangeBoxGeom = (): {
    left: number; top: number; width: number; height: number; hasTime: boolean;
  } | null => {
    const rangeNow = activeRange();
    if (!chartReady() || !rangeNow) return null;
    const series = candleRef.current;
    const el = hostRef.current;
    if (!series || !el) return null;
    const sorted = normalizeCandles(candlesRef.current);
    if (sorted.length) {
      const mid = sorted.reduce((s, c) => s + c.close, 0) / sorted.length;
      if (mid > 0) {
        const ok = (p: number) => p > mid / 50 && p < mid * 50;
        if (!ok(rangeNow.priceLow) || !ok(rangeNow.priceHigh)) return null;
      }
    }
    const y1 = series.priceToCoordinate(rangeNow.priceHigh);
    const y2 = series.priceToCoordinate(rangeNow.priceLow);
    if (y1 == null || y2 == null) return null;

    const hostW = el.clientWidth;
    const hostH = el.clientHeight;
    // Keep grips inside the candle plot — LWC price scale sits on the right ~55px
    const padL = 2;
    const padR = Math.min(64, Math.max(40, Math.round(hostW * 0.08)));
    const padY = 2;
    const plotRight = Math.max(padL + 8, hostW - padR);
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    const topRaw = Math.min(y1, y2);
    const botRaw = Math.max(y1, y2);
    const top = clamp(topRaw, padY, Math.max(padY, hostH - padY - 4));
    const bottom = clamp(botRaw, top + 4, Math.max(top + 4, hostH - padY));
    const height = Math.max(4, bottom - top);

    const hasTime = rangeNow.timeFrom != null && rangeNow.timeTo != null;
    if (!hasTime) {
      return { left: padL, top, width: Math.max(4, plotRight - padL), height, hasTime: false };
    }
    const x1 = timeToX(Math.min(rangeNow.timeFrom!, rangeNow.timeTo!));
    const x2 = timeToX(Math.max(rangeNow.timeFrom!, rangeNow.timeTo!));
    if (x1 == null || x2 == null) {
      return { left: padL, top, width: Math.max(4, plotRight - padL), height, hasTime: false };
    }
    let left = clamp(Math.min(x1, x2), padL, plotRight - 4);
    let right = clamp(Math.max(x1, x2), left + 4, plotRight);
    return { left, top, width: Math.max(4, right - left), height, hasTime: true };
  };

  const syncHandles = () => {
    const chart = chartRef.current;
    const series = candleRef.current;
    const host = hostRef.current;
    const hostW = host?.clientWidth || 200;
    const hostH = host?.clientHeight || 200;
    // Hide grips until the new TF/symbol is fitted — otherwise they fly to garbage coords.
    if (!chart || !series || !chartReady()) { setHandles([]); setDraftPt(null); return; }
    // Same candle-snap as the painted LineSeries — raw times drift and leave “crooked” dots.
    const sorted = normalizeCandles(candlesRef.current);
    const snapTrendTime = (tSec: number) => {
      if (!sorted.length) return Math.floor(Number(tSec) || 0);
      let best = 0;
      let bestD = Math.abs(Number(sorted[0].time) - tSec);
      for (let i = 1; i < sorted.length; i++) {
        const dlt = Math.abs(Number(sorted[i].time) - tSec);
        if (dlt < bestD) { best = i; bestD = dlt; }
      }
      return Number(sorted[best].time);
    };
    const padR = Math.min(64, Math.max(40, Math.round(hostW * 0.08)));
    const plotRight = Math.max(12, hostW - padR);
    /** Never clamp off-plot endpoints onto the right edge — that made “orphan” dots. */
    const inPlot = (x: number, y: number) => (
      x >= 4 && x <= plotRight - 2 && y >= 4 && y <= hostH - 4
    );
    const next: Array<{ id: string; end: string; x: number; y: number; color: string; kind: 'trend' | 'range' | 'hline' }> = [];
    for (const d of payload.drawings) {
      if (d.type === 'trend') {
        const t1 = snapTrendTime(Number(d.t1));
        const t2 = snapTrendTime(Number(d.t2));
        // No visible line when both ends collapse onto one bar — don't leave orphan dots.
        if (!(t1 > 0 && t2 > 0) || t1 === t2) continue;
        // Prefer coordinates from the painted series when present (exact line ends).
        const painted = trendSeriesRef.current.get(d.id);
        let x1 = timeToX(t1);
        let y1 = series.priceToCoordinate(d.p1);
        let x2 = timeToX(t2);
        let y2 = series.priceToCoordinate(d.p2);
        if (painted) {
          try {
            const py1 = painted.priceToCoordinate(d.p1);
            const py2 = painted.priceToCoordinate(d.p2);
            if (py1 != null) y1 = py1;
            if (py2 != null) y2 = py2;
          } catch { /* ignore */ }
        }
        const color = d.color || '#e8edf5';
        // Grips only while drawing/erasing or when this line is selected — never a forest on pointer.
        const showGrips = tool === 'trend' || tool === 'eraser'
          || selectedId === d.id || eraseHit === d.id;
        if (!showGrips) continue;
        if (x1 != null && y1 != null && inPlot(Number(x1), Number(y1))) {
          next.push({ id: d.id, end: 'a', x: Number(x1), y: Number(y1), color, kind: 'trend' });
        }
        if (x2 != null && y2 != null && inPlot(Number(x2), Number(y2))) {
          next.push({ id: d.id, end: 'b', x: Number(x2), y: Number(y2), color, kind: 'trend' });
        }
      }
      if (d.type === 'hline') {
        const y = series.priceToCoordinate(d.price);
        if (y != null) {
          const color = d.color || '#e8edf5';
          // Left / mid / right grips so H-lines are as grabable as trend endpoints
          next.push({ id: d.id, end: 'left', x: Math.max(18, hostW * 0.12), y, color, kind: 'hline' });
          next.push({ id: d.id, end: 'mid', x: hostW * 0.5, y, color, kind: 'hline' });
          next.push({ id: d.id, end: 'right', x: Math.min(hostW - 18, hostW * 0.88), y, color, kind: 'hline' });
        }
      }
    }
    const geom = rangeBoxGeom();
    if (geom) {
      const { left, top, width, height, hasTime } = geom;
      const right = left + width;
      const bottom = top + height;
      const midX = left + width / 2;
      if (hasTime) {
        // Corners + mid-edges: edges resize one axis only (price OR time).
        next.push({ id: RANGE_DRAW_ID, end: 'tl', x: left, y: top, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'tr', x: right, y: top, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'bl', x: left, y: bottom, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'br', x: right, y: bottom, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 't', x: midX, y: top, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'b', x: midX, y: bottom, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'l', x: left, y: top + height / 2, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'r', x: right, y: top + height / 2, color: '#c9a227', kind: 'range' });
      } else {
        next.push({ id: RANGE_DRAW_ID, end: 'hi', x: midX, y: top, color: '#c9a227', kind: 'range' });
        next.push({ id: RANGE_DRAW_ID, end: 'lo', x: midX, y: bottom, color: '#c9a227', kind: 'range' });
      }
    }
    const draft = draftRef.current;
    if (draft?.a) {
      const x = timeToX(draft.a.time);
      const y = series.priceToCoordinate(draft.a.price);
      if (x != null && y != null) setDraftPt({ x, y });
      else setDraftPt(null);
    } else setDraftPt(null);
    // Same grip set → move DOM nodes only (Safari chokes on setState every pan frame).
    const prev = handlesRef.current;
    const same =
      prev.length === next.length
      && prev.every((p, i) => p.id === next[i].id && p.end === next[i].end && p.kind === next[i].kind);
    if (same && next.length && handlesElRef.current.size >= next.length) {
      for (let i = 0; i < next.length; i++) {
        const h = next[i];
        const el = handlesElRef.current.get(`${h.id}:${h.end}`);
        if (!el) continue;
        const left = h.kind === 'range' && (h.end === 't' || h.end === 'b') ? h.x - 14
          : h.kind === 'range' && (h.end === 'l' || h.end === 'r') ? h.x - 4
            : h.x - 6;
        const top = h.kind === 'range' && (h.end === 'l' || h.end === 'r') ? h.y - 14
          : h.kind === 'range' && (h.end === 't' || h.end === 'b') ? h.y - 4
            : h.y - 6;
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
      }
      handlesRef.current = next;
      return;
    }
    setHandles(next);
  };

  const syncRangeBox = () => {
    const geom = rangeBoxGeom();
    if (!geom) {
      if (boxCssRef.current) setBoxCss(null);
      return;
    }
    const next = { left: geom.left, top: geom.top, width: geom.width, height: geom.height };
    const el = rangeBoxElRef.current;
    if (el && boxCssRef.current) {
      el.style.left = `${next.left}px`;
      el.style.top = `${next.top}px`;
      el.style.width = `${next.width}px`;
      el.style.height = `${next.height}px`;
      boxCssRef.current = next;
      return;
    }
    setBoxCss(next);
  };

  /** Coalesce DOM overlay moves onto one rAF so grips/range/OB track pan/zoom live. */
  const overlayRafRef = useRef(0);
  const syncObBoxesRef = useRef<(() => void) | null>(null);
  const scheduleOverlaySync = () => {
    if (overlayRafRef.current) return;
    overlayRafRef.current = requestAnimationFrame(() => {
      overlayRafRef.current = 0;
      syncRangeBox();
      syncHandles();
      syncStrategyStart();
      try { syncObBoxesRef.current?.(); } catch { /* ignore */ }
      // avg entry badge Y — DOM poke while mounted (no React on Safari pan).
      const series = candleRef.current;
      const avg = avgEntryLevel;
      const ep = avg != null ? Number(avg.price) : NaN;
      if (series && Number.isFinite(ep) && ep > 0) {
        const py = series.priceToCoordinate(ep);
        const y = py != null ? Number(py) : null;
        const el = avgEntryElRef.current;
        if (el && y != null && avgEntryYRef.current != null) {
          el.style.top = `${Math.max(28, y - 12)}px`;
          avgEntryYRef.current = y;
        } else if (y !== avgEntryYRef.current) {
          setAvgEntryY(y);
        }
      }
      // Countdown chip under last price — same DOM path.
      const cd = barCdElRef.current;
      if (cd && series) {
        const sorted = normalizeCandles(candlesRef.current);
        if (sorted.length) {
          const py = series.priceToCoordinate(sorted[sorted.length - 1].close);
          if (py != null) {
            const y = Number(py);
            cd.style.top = `${y + 11}px`;
            priceLabelYRef.current = y;
          }
        }
      }
    });
  };

  useEffect(() => {
    if (!hostRef.current) return;
    const el = hostRef.current;
    const th = chartThemeColors(theme);
    // On phone the whole desk pans inside #root — chart must not steal one-finger drag.
    const pagePanDesk = () => {
      try { return window.matchMedia('(max-width: 900px)').matches; } catch { return false; }
    };
    const chart = createChart(el, {
      width: Math.max(el.clientWidth || 640, 320),
      height: Math.max(el.clientHeight || 420, 320),
      layout: {
        background: { type: ColorType.Solid, color: th.bg },
        textColor: th.text,
        fontFamily: 'IBM Plex Mono, monospace',
        panes: { separatorColor: th.border, separatorHoverColor: cssVar('--gold', '#c9a227') },
      },
      grid: {
        vertLines: { color: th.grid },
        horzLines: { color: th.grid },
      },
      rightPriceScale: { borderColor: th.border, autoScale: true },
      timeScale: {
        borderColor: th.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: FUTURE_BARS,
        shiftVisibleRangeOnNewBar: true,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: !pagePanDesk(),
        vertTouchDrag: !pagePanDesk(),
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
      // Normal = free crosshair (not magnet-snapped to OHLC) — better for placing drawings.
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: th.cross, width: 1, style: 2, labelBackgroundColor: th.labelBg },
        horzLine: { color: th.cross, width: 1, style: 2, labelBackgroundColor: th.labelBg },
      },
    });
    const applyDeskTouch = (pan: boolean) => {
      const ta = pan ? 'pan-x pan-y' : 'manipulation';
      try { el.style.touchAction = ta; } catch { /* ignore */ }
      // lightweight-charts paints on nested canvases — they must not eat the phone swipe.
      try {
        el.querySelectorAll('canvas, table, div').forEach((node) => {
          (node as HTMLElement).style.touchAction = ta;
        });
      } catch { /* ignore */ }
    };
    applyDeskTouch(pagePanDesk());
    const onMq = () => {
      const pan = pagePanDesk();
      try {
        chart.applyOptions({
          handleScroll: {
            mouseWheel: true,
            pressedMouseMove: true,
            horzTouchDrag: !pan,
            vertTouchDrag: !pan,
          },
        });
      } catch { /* ignore */ }
      applyDeskTouch(pan);
    };
    let mq: MediaQueryList | null = null;
    try {
      mq = window.matchMedia('(max-width: 900px)');
      mq.addEventListener?.('change', onMq);
      mq.addListener?.(onMq);
    } catch { /* ignore */ }

    const candlesSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#3dba7a',
      downColor: '#e25b5b',
      borderUpColor: '#3dba7a',
      borderDownColor: '#e25b5b',
      wickUpColor: '#3dba7a',
      wickDownColor: '#e25b5b',
      priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
    });
    // Candles fill the main scale. Overlay vol/rsi sit ON the candles (own priceScale),
    // they do not reserve empty bottom margin — only separate basement panes take height.
    const ind0 = payloadRef.current.indicators || {};
    const volOn0 = ind0.volume !== false;
    const rsiOn0 = ind0.rsi !== false;
    candlesSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.04, bottom: 0.04 },
    });
    markersApiRef.current = createSeriesMarkers(candlesSeries, []);

    // Dedicated panes only when the indicator is ON and requested separate.
    const wantVolPane = volOn0 && ind0.volumePane === 'separate';
    const wantRsiPane = rsiOn0 && ind0.rsiPane !== 'main';
    let nextPane = 1;
    const volPane = wantVolPane ? nextPane++ : 0;
    const rsiPane = wantRsiPane ? nextPane++ : 0;
    if (wantVolPane || wantRsiPane) {
      while (chart.panes().length < nextPane) chart.addPane();
      try {
        const panes = chart.panes();
        const basement = Math.max(0, panes.length - 1);
        panes[0]?.setStretchFactor?.(Math.max(4, 6 - basement));
        for (let i = 1; i < panes.length; i++) panes[i]?.setStretchFactor?.(1);
      } catch { /* ignore */ }
    }
    volPaneIndexRef.current = volPane;
    rsiPaneIndexRef.current = rsiPane;

    const volume = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: wantVolPane ? 'right' : 'vol',
    }, volPane);
    if (!wantVolPane) {
      // Overlay band at the foot of the candle pane — candles still use full scale.
      chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } });
    }

    const sma = chart.addSeries(LineSeries, {
      color: '#c9a227', lineWidth: 2, priceLineVisible: false, lastValueVisible: false,
    });
    const ema = chart.addSeries(LineSeries, {
      color: '#5b8def', lineWidth: 2, priceLineVisible: false, lastValueVisible: false,
    });
    const rsi = chart.addSeries(LineSeries, {
      color: '#c084fc', lineWidth: 2,
      priceScaleId: wantRsiPane ? 'right' : 'rsi',
      priceLineVisible: false, lastValueVisible: false,
    }, rsiPane);
    if (!wantRsiPane) {
      chart.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    }

    chartRef.current = chart;
    candleRef.current = candlesSeries;
    volRef.current = volume;
    smaRef.current = sma;
    emaRef.current = ema;
    rsiRef.current = rsi;

    const onRangeChange = (r: LogicalRange | null) => {
      if (!r) return;
      logicalRangeRef.current = r;
      // User pan/zoom (incl. into future whitespace): drop live-pin so we don't snap
      // back to the default ~10% future margin.
      if (!viewportProgrammatic) {
        if (pinRightRef.current || !userInteractedRef.current) {
          userInteractedRef.current = true;
          pinRightRef.current = false;
          setShowJumpLive(true);
        }
      }
      // Near the left edge → ask DeskPage for older bars (skip 1m/5m / busy / exhausted).
      if (
        !pinRightRef.current
        && userInteractedRef.current
        && !historyLoadingRef.current
        && !historyExhaustedRef.current
        && Number.isFinite(r.from)
        && r.from < 12
        && onNeedOlderHistoryRef.current
      ) {
        const now = Date.now();
        if (now - historyAskAtRef.current > 900) {
          historyAskAtRef.current = now;
          onNeedOlderHistoryRef.current();
        }
      }
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRangeChange);
    // Also release pin on wheel / drag — canvas may swallow some pointer moves.
    const releasePin = () => {
      userInteractedRef.current = true;
      pinRightRef.current = false;
      setShowJumpLive(true);
    };
    let panStart: { x: number; y: number } | null = null;
    const onWheel = () => { releasePin(); };
    const onPtrDown = (e: PointerEvent) => {
      panStart = { x: e.clientX, y: e.clientY };
    };
    const onPtrMove = (e: PointerEvent) => {
      if (!panStart) return;
      if (Math.hypot(e.clientX - panStart.x, e.clientY - panStart.y) > 6) {
        releasePin();
        panStart = null;
      }
    };
    const onPtrUp = () => { panStart = null; };
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('pointerdown', onPtrDown);
    el.addEventListener('pointermove', onPtrMove);
    el.addEventListener('pointerup', onPtrUp);
    el.addEventListener('pointercancel', onPtrUp);

    const resize = () => {
      if (!hostRef.current || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: Math.max(hostRef.current.clientWidth, 200),
        height: Math.max(hostRef.current.clientHeight, 280),
      });
      // restore logical range after resize so layout changes don't "jump"
      const saved = logicalRangeRef.current;
      if (saved && userInteractedRef.current) {
        try { chartRef.current.timeScale().setVisibleLogicalRange(saved); } catch { /* ignore */ }
      }
    };
    const ro = new ResizeObserver(() => resize());
    ro.observe(el);
    requestAnimationFrame(resize);

    chart.subscribeCrosshairMove((param: MouseEventParams) => {
      const list = candlesRef.current;
      const fallback = () => {
        const last = list[list.length - 1];
        if (last) {
          setOhlc({
            time: last.time, open: last.open, high: last.high, low: last.low,
            close: last.close, volume: last.volume, source: 'last',
          });
        }
      };
      if (!param.time || !list.length) { fallback(); return; }
      const t = Number(param.time);
      const bar = list.find((c) => normTime(c.time) === t);
      if (bar) {
        setOhlc({
          time: bar.time, open: bar.open, high: bar.high, low: bar.low,
          close: bar.close, volume: bar.volume, source: 'hover',
        });
      } else fallback();
    });

    return () => {
      if (overlayRafRef.current) {
        cancelAnimationFrame(overlayRafRef.current);
        overlayRafRef.current = 0;
      }
      try {
        mq?.removeEventListener?.('change', onMq);
        mq?.removeListener?.(onMq);
      } catch { /* ignore */ }
      ro.disconnect();
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPtrDown);
      el.removeEventListener('pointermove', onPtrMove);
      el.removeEventListener('pointerup', onPtrUp);
      el.removeEventListener('pointercancel', onPtrUp);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange);
      for (const s of trendSeriesRef.current.values()) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      trendSeriesRef.current.clear();
      for (const s of hintTrendRef.current.values()) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      hintTrendRef.current.clear();
      for (const s of fillDashSeriesRef.current) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      fillDashSeriesRef.current = [];
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volRef.current = null;
      smaRef.current = null;
      emaRef.current = null;
      rsiRef.current = null;
      priceLinesRef.current = [];
      hintPriceLinesRef.current = [];
      hintLevelPricesRef.current = [];
      rsiZoneLinesRef.current = [];
    };
  }, []);

  // Keep Lightweight Charts colors in sync with ivory / dark CSS theme (no page refresh).
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const th = chartThemeColors(theme);
    try {
      chart.applyOptions({
        layout: {
          background: { type: ColorType.Solid, color: th.bg },
          textColor: th.text,
          panes: { separatorColor: th.border, separatorHoverColor: cssVar('--gold', '#c9a227') },
        },
        grid: {
          vertLines: { color: th.grid },
          horzLines: { color: th.grid },
        },
        rightPriceScale: { borderColor: th.border },
        timeScale: { borderColor: th.border },
        crosshair: {
          vertLine: { color: th.cross, labelBackgroundColor: th.labelBg },
          horzLine: { color: th.cross, labelBackgroundColor: th.labelBg },
        },
      });
    } catch { /* ignore */ }
  }, [theme]);

  /** Ensure pane N exists (0-based), creating empty panes as needed. */
  const ensurePane = (chart: IChartApi, index: number) => {
    while (chart.panes().length <= index) chart.addPane();
  };

  // TradingView-style panes + candle height: off indicators don't leave empty basement.
  useEffect(() => {
    const chart = chartRef.current;
    const candles = candleRef.current;
    const rsi = rsiRef.current;
    const vol = volRef.current;
    if (!chart || !candles || !rsi || !vol) return;
    const ind = payload.indicators || {};
    const volOn = ind.volume !== false;
    const rsiOn = ind.rsi !== false;
    const wantVolSep = volOn && ind.volumePane === 'separate';
    const wantRsiSep = rsiOn && ind.rsiPane !== 'main';
    try {
      let next = 1;
      const volIdx = wantVolSep ? next++ : 0;
      const rsiIdx = wantRsiSep ? next++ : 0;
      ensurePane(chart, Math.max(volIdx, rsiIdx, 0));
      vol.moveToPane(volIdx);
      vol.applyOptions({ priceScaleId: wantVolSep ? 'right' : 'vol' });
      if (!wantVolSep) {
        try { chart.priceScale('vol', 0).applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } }); }
        catch {
          try { chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } }); } catch { /* ignore */ }
        }
      }
      rsi.moveToPane(rsiIdx);
      rsi.applyOptions({ priceScaleId: wantRsiSep ? 'right' : 'rsi' });
      if (!wantRsiSep) {
        try { chart.priceScale('rsi', 0).applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } }); }
        catch {
          try { chart.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } }); } catch { /* ignore */ }
        }
      }
      // Always fill the main pane; overlay vol/rsi use their own scales (may overlap).
      candles.priceScale().applyOptions({
        scaleMargins: { top: 0.04, bottom: 0.04 },
      });
      // Drop trailing empty panes (LWC keeps them after moveToPane / disabling RSI).
      for (let i = chart.panes().length - 1; i > Math.max(volIdx, rsiIdx, 0); i--) {
        try { chart.removePane(i); } catch { /* ignore */ }
      }
      // Candles dominate height; basement panes stay compact when present.
      try {
        const panes = chart.panes();
        const basement = Math.max(0, panes.length - 1);
        panes[0]?.setStretchFactor?.(Math.max(4, 6 - basement));
        for (let i = 1; i < panes.length; i++) panes[i]?.setStretchFactor?.(1);
      } catch { /* ignore */ }
      volPaneIndexRef.current = volIdx;
      rsiPaneIndexRef.current = rsiIdx;
    } catch (err) {
      console.error('[ChartPane] pane move failed', err);
    }
  }, [
    payload.indicators?.rsiPane,
    payload.indicators?.volumePane,
    payload.indicators?.rsi,
    payload.indicators?.volume,
  ]);

  const clearHintOverlays = () => {
    const series = candleRef.current;
    const chart = chartRef.current;
    const rsi = rsiRef.current;
    if (series) {
      for (const line of hintPriceLinesRef.current) {
        try { series.removePriceLine(line); } catch { /* ignore */ }
      }
      hintPriceLinesRef.current = [];
    }
    if (rsi) {
      for (const line of rsiZoneLinesRef.current) {
        try { rsi.removePriceLine(line); } catch { /* ignore */ }
      }
      rsiZoneLinesRef.current = [];
    }
    hintLevelPricesRef.current = [];
    if (chart) {
      for (const s of hintTrendRef.current.values()) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      hintTrendRef.current.clear();
    }
    setObBoxes([]);
  };

  const wipeSeriesVisuals = () => {
    const series = candleRef.current;
    const chart = chartRef.current;
    if (series) {
      for (const line of priceLinesRef.current) {
        try { series.removePriceLine(line); } catch { /* ignore */ }
      }
      priceLinesRef.current = [];
      try { series.setData([]); } catch { /* ignore */ }
      try { series.priceScale().applyOptions({ autoScale: true }); } catch { /* ignore */ }
    }
    if (chart) {
      try { chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* ignore */ }
      for (const s of trendSeriesRef.current.values()) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      trendSeriesRef.current.clear();
      for (const s of fillDashSeriesRef.current) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      fillDashSeriesRef.current = [];
    }
    clearHintOverlays();
    try { volRef.current?.setData([]); } catch { /* ignore */ }
    try { smaRef.current?.setData([]); } catch { /* ignore */ }
    try { emaRef.current?.setData([]); } catch { /* ignore */ }
    try { rsiRef.current?.setData([]); } catch { /* ignore */ }
    setBoxCss(null);
    setOhlc(null);
  };

  // Symbol/tf change: drop grips (they would fly) but keep last candles on screen until new history arrives.
  // Wiping to empty caused «No data» / one-bar collapse when REST Failed to fetch.
  useEffect(() => {
    fittedKeyRef.current = '';
    userInteractedRef.current = false;
    pinRightRef.current = true;
    setShowJumpLive(false);
    logicalRangeRef.current = null;
    lastBarTimeRef.current = null;
    firstBarTimeRef.current = null;
    barCountRef.current = 0;
    historyAskAtRef.current = 0;
    setHandles([]);
    setBoxCss(null);
    setDraftPt(null);
    setEraseHit(null);
    // Clear price-lines / trends of previous drawings only — leave candle series painted
    const series = candleRef.current;
    const chart = chartRef.current;
    if (series) {
      for (const line of priceLinesRef.current) {
        try { series.removePriceLine(line); } catch { /* ignore */ }
      }
      priceLinesRef.current = [];
    }
    if (chart) {
      for (const s of trendSeriesRef.current.values()) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      trendSeriesRef.current.clear();
      for (const s of fillDashSeriesRef.current) {
        try { chart.removeSeries(s); } catch { /* ignore */ }
      }
      fillDashSeriesRef.current = [];
    }
  }, [seriesKey]);

  // Candles: incremental update for forming bar; full setData only on history/symbol change
  useEffect(() => {
    const series = candleRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;
    // Refuse to paint candles that belong to another symbol:tf (was causing 15m collapse).
    const dataMatches = !candlesKey || candlesKey === seriesKey;
    if (!dataMatches) return;
    if (!candles.length) {
      // Keep previous paint while reconnecting — only wipe if we never fitted this key
      if (fittedKeyRef.current === seriesKey) wipeSeriesVisuals();
      return;
    }

    // Must sort+dedupe: WEEX returns mostly descending times; unsorted overlay setData
    // throws and previously aborted this try before fitContent → empty grid + live OHLC legend.
    const sorted = normalizeCandles(candles);
    if (!sorted.length) return;
    // Ignore tiny/stale payloads after TF switch (one bad bar collapses the viewport).
    if (fittedKeyRef.current !== seriesKey && sorted.length < 20) return;
    const data = withFutureWhitespace(sorted, tfFromSeriesKey());
    const last = sorted[sorted.length - 1];
    const first = sorted[0];
    const lastPoint = toCandlePoint(last);
    const lastTime = Number(lastPoint.time);
    const firstTime = Number(first.time);
    const prevLastTime = lastBarTimeRef.current;
    const prevFirstTime = firstBarTimeRef.current;
    const sameSeries = fittedKeyRef.current === seriesKey;
    // Older bars prepended (scroll-left history) — must full setData, keep time viewport.
    const historyPrepended = sameSeries
      && prevFirstTime != null
      && Number.isFinite(firstTime)
      && firstTime < prevFirstTime
      && sorted.length > barCountRef.current;
    // Never incremental across a TF/symbol change — even if open-times collide.
    const canIncremental =
      sameSeries
      && !historyPrepended
      && prevLastTime != null
      && (lastTime === prevLastTime || lastTime > prevLastTime)
      && sorted.length >= 2;

    const paintOverlays = (incremental: boolean) => {
      const ind = payload.indicators || {};
      try {
        if (volRef.current) {
          if (ind.volume !== false) {
            if (incremental) volRef.current.update(toVolPoint(last));
            else {
              volRef.current.setData(volumePoints(sorted).map((p) => ({
                time: p.time as UTCTimestamp, value: p.value, color: p.color,
              })));
            }
          } else if (!incremental) volRef.current.setData([]);
        }
      } catch (err) {
        console.error('[ChartPane] volume setData failed', err);
      }

      const applyLine = (
        ref: typeof smaRef,
        points: Array<{ time: number; value: number }>,
        enabled: boolean,
        mode: 'update' | 'set',
      ) => {
        if (!ref.current) return;
        if (!enabled) { if (mode === 'set') ref.current.setData([]); return; }
        const line = points.map((p) => ({
          time: p.time as UTCTimestamp, value: p.value,
        })) as LineData[];
        if (!line.length) return;
        if (mode === 'update') ref.current.update(line[line.length - 1]);
        else ref.current.setData(line);
      };

      try {
        // Same forming bar → point update. New bar / full redraw → setData
        // (avoids RSI trailing candles with a right-edge gap after missed updates).
        const mode: 'update' | 'set' = (incremental && lastTime === prevLastTime) ? 'update' : 'set';
        applyLine(smaRef, smaSeries(sorted, ind.smaLen || 20), ind.sma !== false, mode);
        applyLine(emaRef, emaSeries(sorted, ind.emaLen || 50), Boolean(ind.ema), mode);
        applyLine(rsiRef, rsiSeries(sorted, ind.rsiLen || 14), ind.rsi !== false, mode);
      } catch (err) {
        console.error('[ChartPane] overlay setData failed', err);
      }
    };

    try {
      if (canIncremental && lastTime === prevLastTime) {
        series.update(lastPoint);
        paintOverlays(true);
      } else if (canIncremental && lastTime > prevLastTime) {
        series.update(lastPoint);
        paintOverlays(true);
      } else if (historyPrepended) {
        // Keep the user's left-pan: restore the same time window after older bars land.
        let savedTime: { from: UTCTimestamp; to: UTCTimestamp } | null = null;
        try {
          const vr = chart.timeScale().getVisibleRange();
          if (vr && vr.from != null && vr.to != null) {
            savedTime = { from: vr.from as UTCTimestamp, to: vr.to as UTCTimestamp };
          }
        } catch { /* ignore */ }
        const digits = priceDigits(last.close);
        digitsRef.current = digits;
        try {
          series.applyOptions({
            priceFormat: { type: 'price', precision: digits, minMove: minMoveFor(digits) },
          });
        } catch { /* ignore */ }
        series.setData(data as CandlestickData[]);
        paintOverlays(false);
        // setData clears createSeriesMarkers — restore sketch/REAL arrows.
        try { reapplyMarkersRef.current?.(); } catch { /* ignore */ }
        fittedKeyRef.current = seriesKey;
        if (savedTime) {
          try { chart.timeScale().setVisibleRange(savedTime); } catch { /* ignore */ }
        }
        pinRightRef.current = false;
        userInteractedRef.current = true;
        setShowJumpLive(true);
      } else {
        // Never preserve manual price zoom across symbol/history reload — that caused
        // 80k BTC scale stuck on LINK~11 after pair switch.
        const digits = priceDigits(last.close);
        digitsRef.current = digits;
        try {
          series.applyOptions({
            priceFormat: { type: 'price', precision: digits, minMove: minMoveFor(digits) },
          });
        } catch { /* ignore */ }
        try { series.priceScale().applyOptions({ autoScale: true }); } catch { /* ignore */ }
        try { chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* ignore */ }
        series.setData(data as CandlestickData[]);
        // Overlays must not block scroll-to-latest (desc timestamps used to throw here).
        paintOverlays(false);
        try { reapplyMarkersRef.current?.(); } catch { /* ignore */ }
        // Always open on the RIGHT (current price) — never zoom to the strategy range start.
        pinRightRef.current = true;
        userInteractedRef.current = false;
        setShowJumpLive(false);
        scrollChartToLatest(chart, sorted, hostWidth());
        fittedKeyRef.current = seriesKey;
        logicalRangeRef.current = null;
        // Win over drawings/markers effects that re-apply a stale logical range.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (pinRightRef.current && chartRef.current) {
              scrollChartToLatest(
                chartRef.current,
                normalizeCandles(candlesRef.current),
                hostWidth(),
              );
            }
          });
        });
      }

      lastBarTimeRef.current = lastTime;
      firstBarTimeRef.current = firstTime;
      barCountRef.current = sorted.length;
      setOhlc({
        time: last.time, open: last.open, high: last.high, low: last.low,
        close: last.close, volume: last.volume, source: 'last',
      });
      const py = series.priceToCoordinate(last.close);
      setPriceLabelY(py != null ? Number(py) : null);
      setTick((n) => n + 1);
      // Double rAF: price lines / autoscale settle, then grips snap to real coords
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { syncRangeBox(); syncHandles(); });
      });
    } catch (err) {
      console.error('[ChartPane] candle update failed', err);
    }
  }, [candles, seriesKey, candlesKey, payload.indicators]);

  const syncStrategyStart = () => {
    const chart = chartRef.current;
    const raw = Number(strategyStartRef.current);
    if (!chart || !Number.isFinite(raw) || raw <= 0) {
      if (strategyStartXRef.current != null) setStrategyStartX(null);
      return;
    }
    const tSec = Math.floor(raw > 1e12 ? raw / 1000 : raw);
    const sorted = normalizeCandles(candlesRef.current);
    // Snap to nearest candle so the marker always lands on a bar
    let snap = tSec;
    if (sorted.length) {
      let best = sorted[0].time;
      let bestD = Math.abs(best - tSec);
      for (const c of sorted) {
        const d = Math.abs(c.time - tSec);
        if (d < bestD) { best = c.time; bestD = d; }
      }
      snap = best;
    }
    const xRaw = chart.timeScale().timeToCoordinate(snap as UTCTimestamp);
    const x = xRaw != null && Number.isFinite(xRaw) ? Number(xRaw) : null;
    const el = strategyStartElRef.current;
    if (el && x != null && strategyStartXRef.current != null) {
      el.style.left = `${x}px`;
      strategyStartXRef.current = x;
      return;
    }
    if (x !== strategyStartXRef.current) setStrategyStartX(x);
  };

  useEffect(() => {
    scheduleOverlaySync();
    const chart = chartRef.current;
    if (!chart) return;
    const onVis = () => { scheduleOverlaySync(); };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onVis);
    return () => { chart.timeScale().unsubscribeVisibleLogicalRangeChange(onVis); };
  }, [range, tick, strategyStartTime, payload.drawings, tool, selectedId, eraseHit]);

  // Indicator toggles that need full redraw of overlay series without touching candle viewport
  useEffect(() => {
    if (!candles.length) return;
    if (fittedKeyRef.current !== seriesKey) return;
    const sorted = normalizeCandles(candles);
    const ind = payload.indicators || {};
    try {
      if (smaRef.current) {
        if (ind.sma !== false) {
          smaRef.current.setData(smaSeries(sorted, ind.smaLen || 20).map((p) => ({
            time: p.time as UTCTimestamp, value: p.value,
          })));
        } else smaRef.current.setData([]);
      }
      if (emaRef.current) {
        if (ind.ema) {
          emaRef.current.setData(emaSeries(sorted, ind.emaLen || 50).map((p) => ({
            time: p.time as UTCTimestamp, value: p.value,
          })));
        } else emaRef.current.setData([]);
      }
      if (rsiRef.current) {
        if (ind.rsi !== false) {
          rsiRef.current.setData(rsiSeries(sorted, ind.rsiLen || 14).map((p) => ({
            time: p.time as UTCTimestamp, value: p.value,
          })));
        } else rsiRef.current.setData([]);
      }
      if (volRef.current) {
        if (ind.volume !== false) {
          volRef.current.setData(volumePoints(sorted).map((p) => ({
            time: p.time as UTCTimestamp, value: p.value, color: p.color,
          })));
        } else volRef.current.setData([]);
      }
    } catch (err) {
      console.error('[ChartPane] indicator toggle failed', err);
    }
    const chart = chartRef.current;
    if (!chart) return;
    if (pinRightRef.current) {
      scrollChartToLatest(chart, normalizeCandles(candlesRef.current), hostWidth());
      return;
    }
    const saved = logicalRangeRef.current;
    if (saved && userInteractedRef.current) {
      try { chart.timeScale().setVisibleLogicalRange(saved); } catch { /* ignore */ }
    }
  }, [
    payload.indicators?.sma, payload.indicators?.ema, payload.indicators?.rsi, payload.indicators?.volume,
    payload.indicators?.smaLen, payload.indicators?.emaLen, payload.indicators?.rsiLen,
  ]);

  // Hint overlays (RSI zones / auto levels / trends / order blocks) — visual only.
  useEffect(() => {
    const series = candleRef.current;
    const chart = chartRef.current;
    const rsi = rsiRef.current;
    if (!series || !chart || fittedKeyRef.current !== seriesKey) return;
    clearHintOverlays();
    hintLevelPricesRef.current = [];
    const sorted = normalizeCandles(candlesRef.current);
    if (sorted.length < 20) return;
    const ind = payload.indicators || {};
    const sens = Number(ind.hintSensitivity ?? 50);
    const mid = sorted.reduce((s, c) => s + c.close, 0) / sorted.length;
    const priceOk = (p: number) => {
      if (!Number.isFinite(p) || p <= 0) return false;
      if (!Number.isFinite(mid) || mid <= 0) return true;
      return p > mid / 50 && p < mid * 50;
    };

    if (ind.rsiZones && rsi && ind.rsi !== false) {
      const ob = Math.max(55, Math.min(95, Number(ind.rsiOb) || 70));
      const os = Math.max(5, Math.min(45, Number(ind.rsiOs) || 30));
      try {
        rsiZoneLinesRef.current.push(rsi.createPriceLine({
          price: ob, color: 'rgba(226,91,91,0.75)', lineWidth: 1, lineStyle: 2,
          title: 'OB', axisLabelVisible: true,
        }));
        rsiZoneLinesRef.current.push(rsi.createPriceLine({
          price: os, color: 'rgba(61,186,122,0.75)', lineWidth: 1, lineStyle: 2,
          title: 'OS', axisLabelVisible: true,
        }));
      } catch { /* ignore */ }
    }

    if (ind.autoLevels) {
      for (const lvl of autoLevels(sorted, ind.autoLevelsCount ?? 6, 2, sens)) {
        if (!priceOk(lvl.price)) continue;
        const color = lvl.kind === 'high' ? 'rgba(226,91,91,0.55)'
          : lvl.kind === 'low' ? 'rgba(61,186,122,0.55)'
            : 'rgba(201,162,39,0.55)';
        try {
          // ×N = how many times price touched this auto S/R (not an order-block id).
          hintPriceLinesRef.current.push(series.createPriceLine({
            price: lvl.price, color, lineWidth: 1, lineStyle: 3,
            title: `×${lvl.touches}`, axisLabelVisible: true,
          }));
          hintLevelPricesRef.current.push(lvl.price);
        } catch { /* ignore */ }
      }
    }

    if (ind.autoTrends) {
      const snapIdx = (tSec: number) => {
        let best = 0;
        let bestD = Math.abs(Number(sorted[0].time) - tSec);
        for (let i = 1; i < sorted.length; i++) {
          const d = Math.abs(Number(sorted[i].time) - tSec);
          if (d < bestD) { best = i; bestD = d; }
        }
        return best;
      };
      for (const [i, tr] of autoTrends(sorted, ind.autoTrendsCount ?? 2, 2, sens).entries()) {
        if (!priceOk(tr.p1) || !priceOk(tr.p2)) continue;
        // Snap start; allow end into whitespace past last bar for “beyond frame” rays.
        const t1 = Number(sorted[snapIdx(tr.t1)].time);
        let t2 = Number(tr.t2);
        const lastT = Number(sorted[sorted.length - 1].time);
        if (t2 <= lastT) t2 = Number(sorted[snapIdx(t2)].time);
        else {
          const step = Math.max(60, lastT - Number(sorted[Math.max(0, sorted.length - 2)].time));
          t2 = lastT + Math.max(step, Math.round((t2 - lastT) / step) * step);
        }
        if (t1 === t2) continue;
        try {
          const line = chart.addSeries(LineSeries, {
            color: tr.kind === 'up' ? 'rgba(61,186,122,0.85)' : 'rgba(226,91,91,0.85)',
            lineWidth: 2,
            lineStyle: LineStyle.Solid,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
            pointMarkersVisible: false,
          });
          const a = { time: Math.min(t1, t2) as UTCTimestamp, value: t1 < t2 ? tr.p1 : tr.p2 };
          const b = { time: Math.max(t1, t2) as UTCTimestamp, value: t1 < t2 ? tr.p2 : tr.p1 };
          line.setData([a, b]);
          hintTrendRef.current.set(`hint-trend-${i}`, line);
        } catch { /* ignore */ }
      }
    }

    const fmtQty = (n: number) => {
      if (!(n > 0)) return '';
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
      return n >= 10 ? n.toFixed(0) : n.toFixed(2);
    };

    const syncObBoxes = () => {
      if (!ind.orderBlocks && !ind.obBreakers) {
        if (obBoxesRef.current.length) setObBoxes([]);
        return;
      }
      const host = hostRef.current;
      if (!host) {
        if (obBoxesRef.current.length) setObBoxes([]);
        return;
      }
      const hostW = host.clientWidth;
      const hostH = host.clientHeight;
      const padR = Math.min(64, Math.max(40, Math.round(hostW * 0.08)));
      const plotRight = Math.max(10, hostW - padR);
      type ObBox = {
        left: number; top: number; width: number; height: number; side: 'bull' | 'bear';
        metric: string; title: string;
        status: 'fresh' | 'tested' | 'mitigated';
        strength: number; midY?: number;
        buyPct?: number; sellPct?: number; metricTop: number;
        kind: 'ob' | 'breaker';
        priceLow: number; priceHigh: number; midPrice?: number; tFrom: number; tTo: number;
      };
      const bookMode = ind.orderBlocksBook === 'widen' ? 'widen' : 'annotate';
      const depthKey = depth
        ? `${depth.bids?.[0]?.[0]}:${depth.bids?.[0]?.[1]}:${depth.asks?.[0]?.[0]}:${depth.asks?.[0]?.[1]}:${depth.bids?.length}:${depth.asks?.length}`
        : 'nobook';
      const zoneKey = `${seriesKey}|${candlesKey}|${ind.orderBlocksCount ?? 5}|${ind.obBreakersCount ?? 4}|${sens}|${bookMode}|${depthKey}|ob${ind.orderBlocks ? 1 : 0}|brk${ind.obBreakers ? 1 : 0}`;
      let zones = obZonesCacheRef.current?.key === zoneKey
        ? obZonesCacheRef.current.zones
        : null;
      let breakers = obZonesCacheRef.current?.key === zoneKey
        ? obZonesCacheRef.current.breakers
        : null;
      if (!zones || !breakers) {
        zones = ind.orderBlocks
          ? orderBlockHints(sorted, ind.orderBlocksCount ?? 5, sens, depth, bookMode)
          : [];
        breakers = ind.obBreakers
          ? orderBlockBreakers(sorted, ind.obBreakersCount ?? 4, sens, depth, bookMode)
          : [];
        obZonesCacheRef.current = { key: zoneKey, zones, breakers };
      }
      const lastBarT = Number(sorted[sorted.length - 1]?.time);
      const applyBoxDom = (b: ObBox, i: number) => {
        const nodes = obDomRef.current.get(`${b.kind}-${i}`);
        if (!nodes?.box) return false;
        nodes.box.style.left = `${b.left}px`;
        nodes.box.style.top = `${b.top}px`;
        nodes.box.style.width = `${b.width}px`;
        nodes.box.style.height = `${b.height}px`;
        const mid = nodes.box.querySelector('[data-ob-mid]') as HTMLElement | null;
        if (mid) {
          if (b.midY != null && b.midY >= b.top + 2 && b.midY <= b.top + b.height - 2) {
            mid.style.display = 'block';
            mid.style.top = `${b.midY - b.top}px`;
          } else {
            mid.style.display = 'none';
          }
        }
        if (nodes.metric) {
          nodes.metric.style.left = `${Math.max(4, b.left + b.width + 4)}px`;
          nodes.metric.style.top = `${b.metricTop}px`;
        }
        return true;
      };
      // Safari hot path: same cached zones + mounted DOM → reproject coords only (no title rebuild / setState).
      const prev = obBoxesRef.current;
      if (
        obZonesCacheRef.current?.key === zoneKey
        && prev.length
        && prev.every((p) => Number.isFinite(p.priceLow) && Number.isFinite(p.priceHigh))
        && obDomRef.current.size >= prev.length
      ) {
        const metricSlots: number[] = [];
        const placeMetric = (preferY: number): number => {
          let y = Math.max(4, Math.min(hostH - 18, preferY));
          for (let guard = 0; guard < 12; guard++) {
            if (!metricSlots.some((s) => Math.abs(s - y) < 16)) break;
            y = Math.min(hostH - 18, y + 16);
          }
          metricSlots.push(y);
          return y;
        };
        const next: ObBox[] = [];
        let ok = true;
        for (let i = 0; i < prev.length; i++) {
          const p = prev[i];
          const y1 = series.priceToCoordinate(p.priceHigh);
          const y2 = series.priceToCoordinate(p.priceLow);
          if (y1 == null || y2 == null) { ok = false; break; }
          const midPx = p.midPrice ?? (p.priceLow + p.priceHigh) / 2;
          const yMid = series.priceToCoordinate(midPx);
          const x1raw = chart.timeScale().timeToCoordinate(p.tFrom as UTCTimestamp);
          let x2raw = chart.timeScale().timeToCoordinate(p.tTo as UTCTimestamp);
          if (x2raw == null && Number.isFinite(lastBarT)) {
            x2raw = chart.timeScale().timeToCoordinate(lastBarT as UTCTimestamp);
          }
          const x1n = x1raw != null ? Number(x1raw) : 2;
          const x2n = x2raw != null ? Number(x2raw) : plotRight - 2;
          const left = Math.max(2, Math.min(Number.isFinite(x1n) ? x1n : 2, Number.isFinite(x2n) ? x2n : plotRight - 2));
          const right = Math.min(plotRight - 2, Math.max(Number.isFinite(x1n) ? x1n : 2, Number.isFinite(x2n) ? x2n : plotRight - 2));
          let width = right - left;
          if (width < 28) width = Math.min(28, plotRight - 2 - left);
          if (width < 12) { ok = false; break; }
          let top = Math.min(Number(y1), Number(y2));
          let bottom = Math.max(Number(y1), Number(y2));
          if (bottom < 2) { top = 2; bottom = 10; }
          else if (top > hostH - 2) { bottom = hostH - 2; top = hostH - 10; }
          else { top = Math.max(2, top); bottom = Math.min(hostH - 2, bottom); }
          if (bottom - top < 8) {
            const mid = (top + bottom) / 2;
            top = mid - 5;
            bottom = mid + 5;
          }
          const preferY = (yMid != null ? Number(yMid) : (top + bottom) / 2) - 7;
          const b: ObBox = {
            ...p,
            left, top, width, height: bottom - top,
            midY: p.kind === 'breaker' || p.status === 'mitigated'
              ? undefined
              : (yMid != null ? Number(yMid) : undefined),
            metricTop: placeMetric(preferY),
          };
          if (!applyBoxDom(b, i)) { ok = false; break; }
          next.push(b);
        }
        if (ok && next.length === prev.length) {
          obBoxesRef.current = next;
          return;
        }
      }
      const boxes: ObBox[] = [];
      // De-collide metrics chips vertically (LuxAlgo places them at the right edge).
      const metricSlots: number[] = [];
      const placeMetric = (preferY: number): number => {
        let y = Math.max(4, Math.min(hostH - 18, preferY));
        for (let guard = 0; guard < 12; guard++) {
          if (!metricSlots.some((s) => Math.abs(s - y) < 16)) break;
          y = Math.min(hostH - 18, y + 16);
        }
        metricSlots.push(y);
        return y;
      };
      const projectZone = (
        z: (typeof zones)[number],
        kind: 'ob' | 'breaker',
      ) => {
        if (!priceOk(z.priceLow) || !priceOk(z.priceHigh)) return;
        const y1 = series.priceToCoordinate(z.priceHigh);
        const y2 = series.priceToCoordinate(z.priceLow);
        const midPx = z.midPrice ?? (z.priceLow + z.priceHigh) / 2;
        const yMid = series.priceToCoordinate(midPx);
        const x1raw = chart.timeScale().timeToCoordinate(z.tFrom as UTCTimestamp);
        let x2raw = chart.timeScale().timeToCoordinate(z.tTo as UTCTimestamp);
        if (x2raw == null && Number.isFinite(lastBarT)) {
          x2raw = chart.timeScale().timeToCoordinate(lastBarT as UTCTimestamp);
        }
        if (y1 == null || y2 == null) return;
        const x1n = x1raw != null ? Number(x1raw) : 2;
        const x2n = x2raw != null ? Number(x2raw) : plotRight - 2;
        if (!Number.isFinite(x1n) && !Number.isFinite(x2n)) return;
        const left = Math.max(2, Math.min(Number.isFinite(x1n) ? x1n : 2, Number.isFinite(x2n) ? x2n : plotRight - 2));
        const right = Math.min(plotRight - 2, Math.max(Number.isFinite(x1n) ? x1n : 2, Number.isFinite(x2n) ? x2n : plotRight - 2));
        let width = right - left;
        if (width < 28) width = Math.min(28, plotRight - 2 - left);
        if (width < 12) return;
        let top = Math.min(Number(y1), Number(y2));
        let bottom = Math.max(Number(y1), Number(y2));
        if (!Number.isFinite(top) || !Number.isFinite(bottom)) return;
        if (bottom < 2) { top = 2; bottom = 10; }
        else if (top > hostH - 2) { bottom = hostH - 2; top = hostH - 10; }
        else {
          top = Math.max(2, top);
          bottom = Math.min(hostH - 2, bottom);
        }
        if (bottom - top < 8) {
          const mid = (top + bottom) / 2;
          top = mid - 5;
          bottom = mid + 5;
        }
        const hPct = z.heightPct ?? ((z.priceHigh - z.priceLow) / Math.max((z.priceHigh + z.priceLow) / 2, 1e-9)) * 100;
        const bookSide = z.side === 'bull' ? z.bookBidQty : z.bookAskQty;
        const otherSide = z.side === 'bull' ? z.bookAskQty : z.bookBidQty;
        const status = z.status || 'fresh';
        const strength = Math.max(0, Math.min(100, Math.round(Number(z.strength)) || 50));
        const hasBook = (z.bookLevels || 0) > 0 || (bookSide != null && bookSide > 0);
        const buyPct = Math.max(0, Math.min(100, Math.round(Number(z.buyVolPct)) || 50));
        const sellPct = Math.max(0, Math.min(100, Number.isFinite(Number(z.sellVolPct)) ? Math.round(Number(z.sellVolPct)) : (100 - buyPct)));
        const volRaw = Number(z.intervalVol) || Number(z.candleVol) || 0;
        const volTxt = volRaw > 0 ? fmtQty(volRaw) : '';
        const share = Math.max(0, Math.min(100, Math.round(Number(z.volSharePct) || 0)));
        // LuxAlgo metrics: volume + share%. Never print null/undefined into the chip.
        const metricParts: string[] = [];
        if (volTxt) metricParts.push(volTxt);
        if (share > 0) metricParts.push(`${share}%`);
        if (!metricParts.length && bookSide != null && bookSide > 0) metricParts.push(`L2 ${fmtQty(bookSide)}`);
        const metric = metricParts.join('  ');
        const title = [
          kind === 'breaker'
            ? (z.side === 'bull' ? 'Bullish breaker (flipped supply→support)' : 'Bearish breaker (flipped demand→resistance)')
            : (z.side === 'bull' ? 'Bullish order block (demand)' : 'Bearish order block (supply)'),
          `${status} · strength ${strength}/100`,
          `${fmtPrice(z.priceLow)} – ${fmtPrice(z.priceHigh)} (${hPct.toFixed(2)}%)`,
          volTxt ? `interval vol ${volTxt}${share > 0 ? ` · ${share}%` : ''}` : null,
          `buy ${buyPct}% / sell ${sellPct}%`,
          bookSide != null && bookSide > 0 ? `${z.side === 'bull' ? 'bids' : 'asks'} ${fmtQty(bookSide)}` : null,
          otherSide != null && otherSide > 0 ? `opposite ${fmtQty(otherSide)}` : null,
          bookMode === 'widen'
            ? (hasBook ? `${z.bookLevels || 0} book walls` : 'widen needs live book')
            : 'body zone',
        ].filter(Boolean).join('\n');
        const preferY = (yMid != null ? Number(yMid) : (top + bottom) / 2) - 7;
        boxes.push({
          left, top, width, height: bottom - top, side: z.side,
          metric, title, status, strength,
          midY: kind === 'breaker' || status === 'mitigated'
            ? undefined
            : (yMid != null ? Number(yMid) : undefined),
          buyPct, sellPct,
          metricTop: placeMetric(preferY),
          kind,
          priceLow: z.priceLow, priceHigh: z.priceHigh, midPrice: midPx,
          tFrom: z.tFrom, tTo: z.tTo,
        });
      };
      for (const z of zones) projectZone(z, 'ob');
      for (const z of breakers) projectZone(z, 'breaker');
      setObBoxes(boxes);
    };
    syncObBoxes();

    // Position RSI / Volume drag chips near their panes (or overlay bands).
    const placeChips = () => {
      const host = hostRef.current;
      if (!host) { setPaneChips([]); return; }
      const h = host.clientHeight;
      const w = host.clientWidth;
      const chips: Array<{ key: 'rsi' | 'volume'; label: string; left: number; top: number; mode: 'main' | 'separate' }> = [];
      const rsiOn = ind.rsi !== false;
      const volOn = ind.volume !== false;
      const rsiMode = ind.rsiPane === 'main' ? 'main' : 'separate';
      const volMode = ind.volumePane === 'separate' ? 'separate' : 'main';
      try {
        const panes = chart.panes();
        if (rsiOn) {
          const idx = rsiPaneIndexRef.current;
          if (rsiMode === 'separate' && panes[idx]) {
            // Approximate: stack chips by pane order from bottom of host
            const stretch = panes.reduce((s, p) => s + (p.getStretchFactor?.() || 1), 0) || 1;
            let acc = 0;
            for (let i = 0; i < panes.length; i++) {
              const sf = panes[i].getStretchFactor?.() || 1;
              const ph = (sf / stretch) * h;
              if (i === idx) {
                chips.push({ key: 'rsi', label: 'RSI', left: 8, top: Math.max(28, acc + 6), mode: rsiMode });
                break;
              }
              acc += ph;
            }
          } else {
            chips.push({ key: 'rsi', label: 'RSI', left: 8, top: Math.max(28, h * 0.78), mode: rsiMode });
          }
        }
        if (volOn) {
          const idx = volPaneIndexRef.current;
          if (volMode === 'separate' && panes[idx]) {
            const stretch = panes.reduce((s, p) => s + (p.getStretchFactor?.() || 1), 0) || 1;
            let acc = 0;
            for (let i = 0; i < panes.length; i++) {
              const sf = panes[i].getStretchFactor?.() || 1;
              const ph = (sf / stretch) * h;
              if (i === idx) {
                chips.push({ key: 'volume', label: 'Vol', left: Math.min(w - 72, 72), top: Math.max(28, acc + 6), mode: volMode });
                break;
              }
              acc += ph;
            }
          } else {
            chips.push({ key: 'volume', label: 'Vol', left: Math.min(w - 72, 72), top: Math.max(28, h * 0.86), mode: volMode });
          }
        }
      } catch {
        if (rsiOn) chips.push({ key: 'rsi', label: 'RSI', left: 8, top: Math.max(28, h * 0.78), mode: rsiMode });
        if (volOn) chips.push({ key: 'volume', label: 'Vol', left: 72, top: Math.max(28, h * 0.86), mode: volMode });
      }
      setPaneChips(chips);
    };
    placeChips();
    // Hot pan path: only reproject OB boxes (DOM). Chip placement is rare — keep off the rAF.
    syncObBoxesRef.current = () => { syncObBoxes(); };
    scheduleOverlaySync();

    const onVis = () => { scheduleOverlaySync(); };
    const onPtrPan = () => { scheduleOverlaySync(); };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onVis);
    // Safari/WebKit coalesces visible-range callbacks during trackpad pan — drive overlays from
    // pointer/wheel on window (LWC captures the host, so host-only listeners miss many moves).
    const hostEl = hostRef.current;
    let winTracking = false;
    const stopWinTrack = () => {
      if (!winTracking) return;
      winTracking = false;
      window.removeEventListener('pointermove', onPtrPan);
      window.removeEventListener('pointerup', stopWinTrack);
      window.removeEventListener('pointercancel', stopWinTrack);
    };
    const onPtrDown = () => {
      if (winTracking) return;
      winTracking = true;
      window.addEventListener('pointermove', onPtrPan, { passive: true });
      window.addEventListener('pointerup', stopWinTrack, { passive: true });
      window.addEventListener('pointercancel', stopWinTrack, { passive: true });
    };
    hostEl?.addEventListener('pointerdown', onPtrDown, { passive: true });
    hostEl?.addEventListener('wheel', onPtrPan, { passive: true });
    // Safari gesture zoom / momentum scroll after finger lift
    hostEl?.addEventListener('gesturechange', onPtrPan as EventListener, { passive: true } as AddEventListenerOptions);
    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onVis);
      stopWinTrack();
      hostEl?.removeEventListener('pointerdown', onPtrDown);
      hostEl?.removeEventListener('wheel', onPtrPan);
      hostEl?.removeEventListener('gesturechange', onPtrPan as EventListener);
      if (syncObBoxesRef.current) syncObBoxesRef.current = null;
    };
  }, [
    // Do NOT depend on `tick` — candle polls used to rebuild all hint overlays every ~1–2s
    // and freeze Safari for 2–3s after a pan while React caught up.
    candles.length, seriesKey, candlesKey,
    payload.indicators?.rsiZones, payload.indicators?.rsiOb, payload.indicators?.rsiOs,
    payload.indicators?.rsi, payload.indicators?.autoLevels, payload.indicators?.autoLevelsCount,
    payload.indicators?.autoTrends, payload.indicators?.autoTrendsCount,
    payload.indicators?.orderBlocks, payload.indicators?.orderBlocksCount,
    payload.indicators?.orderBlocksBook,
    payload.indicators?.obBreakers, payload.indicators?.obBreakersCount,
    payload.indicators?.hintSensitivity,
    payload.indicators?.rsiPane, payload.indicators?.volumePane, payload.indicators?.volume,
    depth, depth?.bids?.length, depth?.asks?.length,
    depth?.bids?.[0]?.[0], depth?.bids?.[0]?.[1], depth?.asks?.[0]?.[0], depth?.asks?.[0]?.[1],
  ]);

  // Range + drawings — never paint prices from another symbol (e.g. BTC box on LINK).
  // Do NOT depend on `candles`: live polls used to rebuild every trend series ~2s and
  // freeze React clicks (tabs/TF/cabinet) while the LWC canvas still felt alive.
  // Fill ticks use content keys so forming-bar polls do not wipe the time scale.
  const realDashKey = useMemo(
    () => (levelDashes || []).map((d) => `${d.time}:${Number(d.price).toFixed(8)}:${d.kind || ''}`).join('|'),
    [levelDashes],
  );
  const avgKey = avgEntryLevel
    ? `${Number(avgEntryLevel.price).toFixed(8)}:${avgEntryLevel.color || ''}`
    : '';

  useEffect(() => {
    const series = candleRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    // Keep the user's pan/zoom when we only refresh ticks / drawings
    let savedRange: { from: number; to: number } | null = null;
    try {
      const cur = chart.timeScale().getVisibleLogicalRange();
      if (cur && Number.isFinite(cur.from) && Number.isFinite(cur.to)) {
        savedRange = { from: cur.from, to: cur.to };
      }
    } catch { /* ignore */ }

    for (const line of priceLinesRef.current) {
      try { series.removePriceLine(line); } catch { /* ignore */ }
    }
    priceLinesRef.current = [];

    const sorted = normalizeCandles(candlesRef.current);
    const mid = sorted.length
      ? sorted.reduce((s, c) => s + c.close, 0) / sorted.length
      : NaN;
    const priceOk = (p: number) => {
      if (!Number.isFinite(p) || p <= 0) return false;
      if (!Number.isFinite(mid) || mid <= 0) return true;
      // Allow ~50× band; BTC 80k on LINK 11 is ~7000× and must be rejected.
      return p > mid / 50 && p < mid * 50;
    };

    const addLine = (price: number, color: string, title: string) => {
      if (!priceOk(price)) return;
      priceLinesRef.current.push(series.createPriceLine({
        price, color, lineWidth: 1, lineStyle: 2, title, axisLabelVisible: true,
      }));
    };

    if (range && priceOk(range.priceLow) && priceOk(range.priceHigh)) {
      addLine(range.priceLow, '#c9a227', 'range low');
      addLine(range.priceHigh, '#c9a227', 'range high');
    } else {
      setBoxCss(null);
    }
    for (const d of payload.drawings) {
      if (d.type === 'hline') addLine(d.price, d.color || '#8b97a8', d.title || 'H');
      if (d.type === 'range' && priceOk(d.priceLow) && priceOk(d.priceHigh)) {
        addLine(d.priceLow, '#dfb83a', 'draw low');
        addLine(d.priceHigh, '#dfb83a', 'draw high');
      }
    }

    for (const s of trendSeriesRef.current.values()) {
      try { chart.removeSeries(s); } catch { /* ignore */ }
    }
    trendSeriesRef.current.clear();
    for (const s of fillDashSeriesRef.current) {
      try { chart.removeSeries(s); } catch { /* ignore */ }
    }
    fillDashSeriesRef.current = [];

    // Snap any overlay time onto an existing candle — off-grid times inflate LWC's
    // logical index space and yank tabs-with-range away from live.
    const snapIdx = (tSec: number) => {
      if (!sorted.length) return 0;
      let best = 0;
      let bestD = Math.abs(Number(sorted[0].time) - tSec);
      for (let i = 1; i < sorted.length; i++) {
        const d = Math.abs(Number(sorted[i].time) - tSec);
        if (d < bestD) { best = i; bestD = d; }
      }
      return best;
    };
    const snapTime = (tSec: number) => Number(sorted[snapIdx(tSec)]?.time || Math.floor(tSec));

    for (const d of payload.drawings) {
      if (d.type !== 'trend') continue;
      if (!priceOk(d.p1) || !priceOk(d.p2)) continue;
      const hit = eraseHit === d.id;
      const sel = selectedId === d.id;
      const line = chart.addSeries(LineSeries, {
        color: hit ? '#e25b5b' : sel ? '#dfb83a' : (d.color || '#e8edf5'),
        lineWidth: hit || sel ? 3 : 2,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        pointMarkersVisible: false,
      });
      const tt1 = snapTime(Number(d.t1));
      const tt2 = snapTime(Number(d.t2));
      if (tt1 === tt2) continue;
      // Keep endpoint prices tied to their snapped times (don't swap values on sort).
      const a = { time: tt1 as UTCTimestamp, value: d.p1 };
      const b = { time: tt2 as UTCTimestamp, value: d.p2 };
      line.setData(tt1 <= tt2 ? [a, b] : [b, a]);
      trendSeriesRef.current.set(d.id, line);
    }

    // Entry / add / exit — short ticks on EXISTING candle times only.
    const addTick = (tSec: number, price: number, color: string) => {
      if (!priceOk(price) || !tSec || sorted.length < 2) return;
      const i = snapIdx(tSec);
      const i0 = Math.max(0, i - 1);
      const i1 = Math.min(sorted.length - 1, i === 0 ? 1 : i + 1);
      const t0 = Number(sorted[i0].time) as UTCTimestamp;
      const t1 = Number(sorted[i1].time) as UTCTimestamp;
      if (!(t1 > t0)) return;
      const line = chart.addSeries(LineSeries, {
        color,
        lineWidth: 1, // thin — must not paint over candle bodies
        lineStyle: 0, // solid short tick
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        pointMarkersVisible: false,
      });
      line.setData([
        { time: t0, value: price },
        { time: t1, value: price },
      ]);
      fillDashSeriesRef.current.push(line);
    };
    const seen = new Set<string>();
    const paintDashList = (list: typeof levelDashes) => {
      for (const lv of list || []) {
        const p = Number(lv.price);
        const t = Number(lv.time) || 0;
        if (!priceOk(p) || !t) continue;
        const key = `${t}:${p.toFixed(8)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        addTick(t, p, lv.color || '#f5d76e');
      }
    };
    // Cap sketch ticks so a dense range cannot spawn 100 LineSeries
    const sketchCap = (sketchDashes || []).length > 36
      ? (sketchDashes || []).filter((_, i, arr) => i % Math.ceil(arr.length / 36) === 0
        || arr[i].kind === 'exit'
        || (arr[i].kind === 'entry' && !String(arr[i].title || '').includes('+')))
      : (sketchDashes || []);
    paintDashList(sketchCap);
    paintDashList(levelDashes);
    if (avgEntryLevel && priceOk(Number(avgEntryLevel.price)) && sorted.length) {
      const ep = Number(avgEntryLevel.price);
      const col = avgEntryLevel.color || '#f5d76e';
      const title = avgEntryLevel.title || 'avg';
      // Full-width entry line (stronger when selected) + last-bar tick
      priceLinesRef.current.push(series.createPriceLine({
        price: ep,
        color: col,
        lineWidth: avgEntryLevel.selected ? 2 : 1,
        lineStyle: avgEntryLevel.selected ? 0 : 2,
        title,
        axisLabelVisible: true,
      }));
      addTick(Number(sorted[sorted.length - 1].time), ep, col);
      const py = series.priceToCoordinate(ep);
      setAvgEntryY(py != null ? Number(py) : null);
    } else {
      setAvgEntryY(null);
    }

    // Eraser / selection highlight for H-lines
    const highlightId = eraseHit || selectedId;
    if (highlightId) {
      const hit = payload.drawings.find((d) => d.id === highlightId);
      if (hit?.type === 'hline' && priceOk(hit.price)) {
        addLine(hit.price, eraseHit === hit.id ? '#e25b5b' : '#dfb83a', eraseHit === hit.id ? 'del' : 'sel');
      }
    }

    // Pin to realtime-right after tab/history load. Only restore a saved pan after the user moved.
    // (Restoring unconditionally jumped back to the strategy-range start.)
    const applyViewport = () => {
      if (fittedKeyRef.current !== seriesKey) return;
      if (pinRightRef.current || !userInteractedRef.current) {
        scrollChartToLatest(chart, normalizeCandles(candlesRef.current), hostWidth());
        return;
      }
      if (savedRange) {
        try { chart.timeScale().setVisibleLogicalRange(savedRange); } catch { /* ignore */ }
      }
    };
    applyViewport();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyViewport();
        syncRangeBox();
        syncHandles();
      });
    });
  }, [payload.drawings, range, seriesKey, eraseHit, selectedId, candles.length > 0, realDashKey, sketchDashKey, avgKey]);

  const markerKey = useMemo(() => {
    const sk = (backtestMarkers || []).map((m) => `${m.time}:${m.side}:${m.label}`).join('|');
    const rv = (liveMarkers || []).map((m) => `${m.time}:${m.side}:${m.price}`).join('|');
    return `${sk}#${rv}#${realDashKey}`;
  }, [backtestMarkers, liveMarkers, realDashKey]);

  // Sketch = sk L / sk +L / sk ×L. Real = R / R+ / Rx. Do not re-run on every forming candle.
  useEffect(() => {
    const api = markersApiRef.current;
    if (!api) {
      reapplyMarkersRef.current = null;
      return;
    }
    const paint = () => {
      const cur = markersApiRef.current;
      if (!cur) return;
      const times = normalizeCandles(candlesRef.current)
        .map((c) => Math.floor(Number(c.time) || 0))
        .filter((t) => t > 0);
      const snap = (tRaw: number) => {
        const t = Math.floor(tRaw);
        if (!times.length) return t;
        let best = times[0];
        let bestDist = Math.abs(best - t);
        for (let i = 1; i < times.length; i++) {
          const d = Math.abs(times[i] - t);
          if (d < bestDist) { best = times[i]; bestDist = d; }
        }
        return best;
      };
      // Soft cyan long / peach short — distinct from candle green/red so OHLC stays visible.
      // Snap sketch times to bar opens — LWC drops markers whose time is not on a candle.
      const sketch: SeriesMarker<UTCTimestamp>[] = (backtestMarkers || []).map((m) => {
        const exit = m.side === 'exit';
        const long = m.side === 'long';
        const add = Boolean(m.label && m.label.includes('+'));
        return {
          time: snap(Number(m.time) || 0) as UTCTimestamp,
          position: exit || !long ? 'aboveBar' : 'belowBar',
          color: long ? '#6ec8ff' : exit ? '#d4b85a' : '#f0a070',
          shape: exit || add ? 'circle' : long ? 'arrowUp' : 'arrowDown',
          text: m.label ? `sk ${m.label}` : 'sk',
        };
      });
      const kindAt = (t: number, price: number) => {
        const hit = (levelDashes || []).find((d) =>
          Math.abs(Number(d.time) - t) <= 90
          && Math.abs(Number(d.price) - price) / Math.max(Math.abs(price), 1e-12) < 5e-4);
        return hit?.kind || null;
      };
      // REAL markers drawn last + distinct cyan so they aren't lost under sketch yellow.
      const real: SeriesMarker<UTCTimestamp>[] = (liveMarkers || []).map((m) => {
        const t = snap(Number(m.time) || 0);
        const kind = kindAt(t, Number(m.price) || 0) || kindAt(Math.floor(Number(m.time) || 0), Number(m.price) || 0);
        const exit = m.side === 'exit' || kind === 'exit';
        const add = kind === 'add';
        const long = m.side === 'long' && !exit;
        const label = exit ? 'REAL ×' : add ? 'REAL +' : 'REAL';
        return {
          time: t as UTCTimestamp,
          position: long ? 'belowBar' : 'aboveBar',
          color: exit ? '#ff8a5c' : '#5eead4',
          shape: exit ? 'square' : long ? 'arrowUp' : 'arrowDown',
          text: label,
        };
      });
      try { cur.setMarkers([...sketch, ...real]); } catch { /* ignore */ }
    };
    reapplyMarkersRef.current = paint;
    paint();
    return () => { reapplyMarkersRef.current = null; };
  }, [markerKey, seriesKey, backtestMarkers, liveMarkers, levelDashes]);

  const distToSegment = (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy || 1;
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const nx = x1 + t * dx;
    const ny = y1 + t * dy;
    return Math.hypot(px - nx, py - ny);
  };

  const hitDrawing = (
    price: number,
    time: number,
    point: { x: number; y: number },
    boxHalf = 0,
  ): Drawing | null => {
    const chart = chartRef.current;
    const series = candleRef.current;
    if (!chart || !series) return null;
    const half = Math.max(0, boxHalf);
    const pxTol = Math.max(Math.abs(price) * 0.004, minMoveFor(digitsRef.current) * 8);
    const left = point.x - half;
    const right = point.x + half;
    const top = point.y - half;
    const bottom = point.y + half;
    for (const d of payload.drawings) {
      if (d.type === 'hline') {
        const y = series.priceToCoordinate(d.price);
        if (half > 0 && y != null) {
          if (Number(y) >= top && Number(y) <= bottom) return d;
        } else if (Math.abs(d.price - price) <= pxTol) return d;
      }
      if (d.type === 'range') {
        const yHi = series.priceToCoordinate(d.priceHigh);
        const yLo = series.priceToCoordinate(d.priceLow);
        if (half > 0 && yHi != null && yLo != null) {
          const rTop = Math.min(Number(yHi), Number(yLo));
          const rBot = Math.max(Number(yHi), Number(yLo));
          const yHit = !(bottom < rTop || top > rBot);
          let xHit = true;
          if (d.timeFrom != null && d.timeTo != null) {
            const x1 = timeToX(Math.min(d.timeFrom, d.timeTo));
            const x2 = timeToX(Math.max(d.timeFrom, d.timeTo));
            if (x1 != null && x2 != null) {
              const rL = Math.min(Number(x1), Number(x2));
              const rR = Math.max(Number(x1), Number(x2));
              xHit = !(right < rL || left > rR);
            }
          }
          if (yHit && xHit) return d;
        } else if (price >= d.priceLow - pxTol && price <= d.priceHigh + pxTol) {
          if (d.timeFrom != null && d.timeTo != null) {
            if (time >= Math.min(d.timeFrom, d.timeTo) && time <= Math.max(d.timeFrom, d.timeTo)) return d;
          } else return d;
        }
      }
      if (d.type === 'trend') {
        const x1 = chart.timeScale().timeToCoordinate(Math.floor(d.t1) as UTCTimestamp);
        const y1 = series.priceToCoordinate(d.p1);
        const x2 = chart.timeScale().timeToCoordinate(Math.floor(d.t2) as UTCTimestamp);
        const y2 = series.priceToCoordinate(d.p2);
        if (x1 == null || y1 == null || x2 == null || y2 == null) continue;
        const lim = half > 0 ? half : 10;
        if (distToSegment(point.x, point.y, x1, y1, x2, y2) < lim) return d;
      }
    }
    return null;
  };

  const disableIndicator = (key: EraseIndKey) => {
    const ind = payloadRef.current.indicators || {};
    onPayloadChange({
      ...payloadRef.current,
      indicators: { ...ind, [key]: false },
    });
  };

  /** Hit-test visible indicators for the eraser (SMA/EMA/RSI/Vol/structure). */
  const hitIndicator = (price: number, point: { x: number; y: number }): EraseIndKey | null => {
    const chart = chartRef.current;
    const series = candleRef.current;
    const ind = payloadRef.current.indicators || {};
    if (!chart || !series) return null;
    const host = hostRef.current;
    const hostH = host?.clientHeight || 400;

    const tip = tool === 'eraser' ? ERASER_HALF : 8;
    for (const b of obBoxesRef.current) {
      if (
        point.x >= b.left - tip && point.x <= b.left + b.width + tip
        && point.y >= b.top - tip && point.y <= b.top + b.height + tip
      ) {
        if (b.kind === 'breaker' && ind.obBreakers) return 'obBreakers';
        if (b.kind !== 'breaker' && ind.orderBlocks) return 'orderBlocks';
      }
    }
    if (ind.autoLevels) {
      for (const lp of hintLevelPricesRef.current) {
        const y = series.priceToCoordinate(lp);
        if (y != null && Math.abs(point.y - Number(y)) <= tip) return 'autoLevels';
      }
    }

    if (ind.autoTrends && hintTrendRef.current.size) {
      // Any click near dashed structure trends (use level-ish y band of last candle mid)
      const y = series.priceToCoordinate(price);
      if (y != null) {
        for (const s of hintTrendRef.current.values()) {
          try {
            const py = s.priceToCoordinate(price);
            if (py != null && Math.abs(point.y - Number(py)) <= 10) return 'autoTrends';
          } catch { /* ignore */ }
        }
        // Fallback: lower third of main pane while trends are on
        if (point.y > hostH * 0.15 && point.y < hostH * 0.7 && Math.abs(point.y - Number(y)) <= 6) {
          /* keep scanning — don't false-positive on price alone */
        }
      }
      // Screen-space proximity via series last value isn't reliable; use OB-style: if near any tip chip
    }

    const last = candlesRef.current[candlesRef.current.length - 1];
    if (last && ind.sma !== false && smaRef.current) {
      try {
        const pts = smaSeries(normalizeCandles(candlesRef.current), ind.smaLen || 20);
        const v = pts[pts.length - 1]?.value;
        if (v != null) {
          const y = series.priceToCoordinate(v);
          if (y != null && Math.abs(point.y - Number(y)) <= tip) return 'sma';
        }
      } catch { /* ignore */ }
    }
    if (last && ind.ema && emaRef.current) {
      try {
        const pts = emaSeries(normalizeCandles(candlesRef.current), ind.emaLen || 50);
        const v = pts[pts.length - 1]?.value;
        if (v != null) {
          const y = series.priceToCoordinate(v);
          if (y != null && Math.abs(point.y - Number(y)) <= tip) return 'ema';
        }
      } catch { /* ignore */ }
    }

    // RSI / Volume: hit their chip bands or separate-pane vertical bands
    for (const chip of paneChipsRef.current) {
      if (
        point.x >= chip.left - 4 && point.x <= chip.left + 56
        && point.y >= chip.top - 4 && point.y <= chip.top + 28
      ) {
        return chip.key === 'rsi' ? 'rsi' : 'volume';
      }
    }
    if (ind.rsi !== false) {
      const rsiSep = ind.rsiPane !== 'main';
      if (rsiSep && point.y > hostH * 0.72) return 'rsi';
      if (!rsiSep && point.y > hostH * 0.86) return 'rsi';
    }
    if (ind.volume !== false) {
      const volSep = ind.volumePane === 'separate';
      if (volSep && point.y > hostH * 0.55 && point.y < hostH * 0.78) return 'volume';
      if (!volSep && point.y > hostH * 0.88) return 'volume';
    }

    // Auto-trends: click near any dashed hint series endpoint price
    if (ind.autoTrends) {
      for (const s of hintTrendRef.current.values()) {
        try {
          // Approximate: if mouse price is close to series coordinateToPrice
          const py = s.coordinateToPrice?.(point.y);
          if (py != null && Number.isFinite(Number(py)) && Math.abs(Number(py) - price) / Math.max(price, 1e-9) < 0.004) {
            return 'autoTrends';
          }
        } catch { /* ignore */ }
      }
    }
    return null;
  };

  // Drawing clicks + eraser highlight + free crosshair overlay
  useEffect(() => {
    const chart = chartRef.current;
    const series = candleRef.current;
    const el = hostRef.current;
    if (!chart || !series || !el) return;

    const onMove = (param: MouseEventParams) => {
      if (param.point) setCross({ x: param.point.x, y: param.point.y });
      else setCross(null);
      if (tool === 'eraser' && param.point) {
        const price = series.coordinateToPrice(param.point.y);
        const time = param.time != null ? Number(param.time) : xToTime(param.point.x);
        if (price != null && time != null) {
          const hit = hitDrawing(Number(price), time, param.point, ERASER_HALF);
          setEraseHit(hit?.id || null);
          if (!hit) setEraseIndHit(hitIndicator(Number(price), param.point));
          else setEraseIndHit(null);
        }
      } else if (tool !== 'eraser') {
        setEraseHit(null);
        setEraseIndHit(null);
      }
    };

    const handler = (param: MouseEventParams) => {
      if (!param.point) return;
      const price = series.coordinateToPrice(param.point.y);
      if (price == null || !Number.isFinite(price)) return;
      // Future zone: LWC leaves param.time null — extrapolate from x / whitespace.
      const time = param.time != null ? Number(param.time) : xToTime(param.point.x);
      if (time == null || !Number.isFinite(time)) return;

      if (tool === 'pointer') {
        // Open-position avg entry — click near the line to open position tools
        const avg = avgEntryRef.current;
        if (avg && onAvgEntryClickRef.current && Number.isFinite(avg.price) && avg.price > 0) {
          const ay = series.priceToCoordinate(avg.price);
          if (ay != null && Math.abs(param.point.y - Number(ay)) <= 10) {
            onAvgEntryClickRef.current();
            onSelect?.(null);
            return;
          }
        }
        if (range && boxCss) {
          const inBox = param.point.x >= boxCss.left && param.point.x <= boxCss.left + boxCss.width
            && param.point.y >= boxCss.top && param.point.y <= boxCss.top + boxCss.height;
          if (inBox) {
            onSelect?.(RANGE_DRAW_ID);
            return;
          }
        }
        const hit = hitDrawing(Number(price), time, param.point);
        onSelect?.(hit?.id || null);
        return;
      }

      if (tool === 'eraser') {
        const eh = ERASER_HALF;
        // Hit strategy range box first (square eraser intersects the yellow box)
        if (range && boxCss && param.point) {
          const erL = param.point.x - eh;
          const erR = param.point.x + eh;
          const erT = param.point.y - eh;
          const erB = param.point.y + eh;
          const hitRange = !(erR < boxCss.left || erL > boxCss.left + boxCss.width
            || erB < boxCss.top || erT > boxCss.top + boxCss.height);
          if (hitRange) {
            onRangeClear?.();
            onSelect?.(null);
            setEraseHit(null);
            setEraseIndHit(null);
            return;
          }
        }
        const hit = hitDrawing(Number(price), time, param.point, eh);
        if (hit) {
          if (hit.type === 'range') onRangeClear?.();
          onPayloadChange({ ...payload, drawings: payload.drawings.filter((d) => d.id !== hit.id) });
          onSelect?.(null);
          setEraseHit(null);
          setEraseIndHit(null);
          return;
        }
        const indHit = hitIndicator(Number(price), param.point);
        if (indHit) {
          disableIndicator(indHit);
          setEraseIndHit(null);
        }
        return;
      }

      if (tool === 'hline') {
        const next: Drawing = { id: newId(), type: 'hline', price: Number(price), color: drawColor };
        onPayloadChange({ ...payload, drawings: [...payload.drawings, next] });
        onSelect?.(next.id);
        return;
      }

      if (tool === 'range') {
        const draft = draftRef.current;
        if (!draft || draft.kind !== 'range' || !draft.a) {
          draftRef.current = { kind: 'range', a: { time, price: Number(price) } };
          syncHandles();
          return;
        }
        const low = Math.min(draft.a.price, Number(price));
        const high = Math.max(draft.a.price, Number(price));
        draftRef.current = null;
        setDraftPt(null);
        const box = !rangeUseTime
          ? { priceLow: low, priceHigh: high, timeFrom: null, timeTo: null }
          : {
              priceLow: low,
              priceHigh: high,
              timeFrom: Math.min(draft.a.time, time),
              timeTo: Math.max(draft.a.time, time),
            };
        const next: Drawing = { id: newId(), type: 'range', ...box };
        onPayloadChange({ ...payload, drawings: [...payload.drawings.filter((d) => d.type !== 'range'), next] });
        onRangePick?.(box);
        return;
      }

      if (tool === 'trend') {
        const draft = draftRef.current;
        const sorted = normalizeCandles(candlesRef.current);
        const snapT = (raw: number) => {
          if (!sorted.length) return Math.floor(Number(raw) || 0);
          let best = 0;
          let bestD = Math.abs(Number(sorted[0].time) - raw);
          for (let i = 1; i < sorted.length; i++) {
            const dlt = Math.abs(Number(sorted[i].time) - raw);
            if (dlt < bestD) { best = i; bestD = dlt; }
          }
          return Number(sorted[best].time);
        };
        if (!draft || draft.kind !== 'trend' || !draft.a) {
          draftRef.current = { kind: 'trend', a: { time: snapT(time), price: Number(price) } };
          syncHandles();
          return;
        }
        const next: Drawing = {
          id: newId(), type: 'trend',
          t1: snapT(draft.a.time), p1: draft.a.price, t2: snapT(time), p2: Number(price),
          color: drawColor,
        };
        draftRef.current = null;
        setDraftPt(null);
        onPayloadChange({ ...payload, drawings: [...payload.drawings, next] });
        onSelect?.(next.id);
      }
    };

    chart.subscribeClick(handler);
    chart.subscribeCrosshairMove(onMove);
    return () => {
      chart.unsubscribeClick(handler);
      chart.unsubscribeCrosshairMove(onMove);
    };
  }, [tool, payload, onPayloadChange, onRangePick, onRangeClear, drawColor, rangeUseTime, range, boxCss, onSelect]);

  // Delete / Backspace removes the selected drawing (or strategy range); Escape clears ruler
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (ev.target as HTMLElement)?.isContentEditable) return;
      if (ev.key === 'Escape' && rulerRef.current) {
        setRuler(null);
        return;
      }
      if (ev.key !== 'Delete' && ev.key !== 'Backspace') return;
      if (!selectedId) return;
      ev.preventDefault();
      if (selectedId === RANGE_DRAW_ID) {
        onRangeClear?.();
        onSelect?.(null);
        return;
      }
      const next = {
        ...payloadRef.current,
        drawings: payloadRef.current.drawings.filter((d) => d.id !== selectedId),
      };
      onPayloadChange(next);
      onSelect?.(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, onPayloadChange, onRangeClear, onSelect]);

  // Clear ephemeral ruler when leaving the tool / switching symbol
  useEffect(() => {
    if (tool !== 'ruler') setRuler(null);
  }, [tool, seriesKey]);

  // Re-place trend grips when tool / selection changes (hidden unless editing).
  useEffect(() => {
    syncHandles();
  }, [tool, selectedId, eraseHit, payload.drawings, range]);

  // TradingView-like measuring ruler: press-drag-release (time + % + price delta)
  useEffect(() => {
    const el = hostRef.current;
    const series = candleRef.current;
    if (!el || !series || tool !== 'ruler') return;

    const pointFromEvent = (ev: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      const price = series.coordinateToPrice(y);
      const time = xToTime(x);
      if (price == null || time == null || !Number.isFinite(Number(price)) || !Number.isFinite(time)) return null;
      return { time, price: Number(price), x, y };
    };

    const onDown = (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      const pt = pointFromEvent(ev);
      if (!pt) return;
      ev.preventDefault();
      try { el.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
      setRuler({ t1: pt.time, p1: pt.price, t2: pt.time, p2: pt.price, dragging: true });
    };
    const onMove = (ev: PointerEvent) => {
      const cur = rulerRef.current;
      if (!cur?.dragging) return;
      const pt = pointFromEvent(ev);
      if (!pt) return;
      setRuler({ ...cur, t2: pt.time, p2: pt.price, dragging: true });
    };
    const onUp = (ev: PointerEvent) => {
      const cur = rulerRef.current;
      if (!cur?.dragging) return;
      const pt = pointFromEvent(ev);
      setRuler({
        t1: cur.t1,
        p1: cur.p1,
        t2: pt?.time ?? cur.t2,
        p2: pt?.price ?? cur.p2,
        dragging: false,
      });
      try { el.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [tool, tick, seriesKey]);

  // Drag H-lines / trend endpoints / range corners (also grab H-line body in pointer mode)
  useEffect(() => {
    const el = hostRef.current;
    const series = candleRef.current;
    const chart = chartRef.current;
    if (!el || !series || !chart) return;

    const onUp = () => {
      const drag = dragRef.current;
      if (drag && dragDirtyRef.current) {
        if (drag.kind === 'range' && rangeDraftRef.current) {
          // One parent commit at end of gesture — avoids PATCH/history/backtest every move.
          onRangePick?.(rangeDraftRef.current);
          rangeDraftRef.current = null;
        } else if (drag.kind !== 'range') {
          // Commit one history step after a drawing drag gesture
          onPayloadChange(payloadRef.current, { history: true });
        }
      }
      dragRef.current = null;
      dragDirtyRef.current = false;
      rangeDraftRef.current = null;
    };
    const onDown = (ev: PointerEvent) => {
      // Body-grab only in pointer mode so hline tool still places new levels
      if (tool !== 'pointer') return;
      if (dragRef.current) return;
      const rect = el.getBoundingClientRect();
      const y = ev.clientY - rect.top;
      const price = series.coordinateToPrice(y);
      if (price == null) return;
      const tol = Math.max(Math.abs(Number(price)) * 0.003, minMoveFor(digitsRef.current) * 6);
      for (const d of payload.drawings) {
        if (d.type === 'hline' && Math.abs(d.price - Number(price)) <= tol) {
          dragRef.current = { kind: 'hline', id: d.id, end: 'mid' };
          onSelect?.(d.id);
          ev.preventDefault();
          break;
        }
      }
    };
    const onMove = (ev: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const rect = el.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      const price = series.coordinateToPrice(y);
      if (price == null) return;
      dragDirtyRef.current = true;
      if (drag.kind === 'hline') {
        onPayloadChange({
          ...payloadRef.current,
          drawings: payloadRef.current.drawings.map((d) => (
            d.id === drag.id && d.type === 'hline' ? { ...d, price: Number(price) } : d
          )),
        }, { history: false });
        return;
      }
      if (drag.kind === 'trend') {
        const tRaw = xToTime(x);
        if (tRaw == null) return;
        // Snap to a real candle so handles stay on the painted line (no orphan dots).
        const sorted = normalizeCandles(candlesRef.current);
        let t = Math.floor(Number(tRaw));
        if (sorted.length) {
          let best = 0;
          let bestD = Math.abs(Number(sorted[0].time) - t);
          for (let i = 1; i < sorted.length; i++) {
            const dlt = Math.abs(Number(sorted[i].time) - t);
            if (dlt < bestD) { best = i; bestD = dlt; }
          }
          t = Number(sorted[best].time);
        }
        onPayloadChange({
          ...payloadRef.current,
          drawings: payloadRef.current.drawings.map((d) => {
            if (d.id !== drag.id || d.type !== 'trend') return d;
            if (drag.end === 'a') return { ...d, t1: t, p1: Number(price) };
            return { ...d, t2: t, p2: Number(price) };
          }),
        }, { history: false });
        return;
      }
      // range corners / mid-edges — local draft only; parent commit on pointerup
      const base = rangeDraftRef.current || rangeRef.current;
      if (!base) return;
      const next = { ...base };
      const p = Number(price);
      const minP = minMoveFor(digitsRef.current);
      // Horizontal edges (and corner tops/bottoms): price only on t/b/hi/lo
      if (drag.end === 'hi' || drag.end === 't' || drag.end === 'tl' || drag.end === 'tr') {
        next.priceHigh = Math.max(p, next.priceLow + minP);
      }
      if (drag.end === 'lo' || drag.end === 'b' || drag.end === 'bl' || drag.end === 'br') {
        next.priceLow = Math.min(p, next.priceHigh - minP);
      }
      // Vertical edges (and corner left/right): time only on l/r; corners also move time
      if (next.timeFrom != null && next.timeTo != null) {
        const t = xToTime(x);
        if (t != null) {
          if (drag.end === 'l' || drag.end === 'tl' || drag.end === 'bl') {
            next.timeFrom = Math.min(t, next.timeTo!);
          }
          if (drag.end === 'r' || drag.end === 'tr' || drag.end === 'br') {
            next.timeTo = Math.max(t, next.timeFrom!);
          }
        }
      }
      // Pure edge: t/b ignore time; l/r ignore price (re-apply from base)
      if (drag.end === 't' || drag.end === 'b' || drag.end === 'hi' || drag.end === 'lo') {
        next.timeFrom = base.timeFrom;
        next.timeTo = base.timeTo;
      }
      if (drag.end === 'l' || drag.end === 'r') {
        next.priceLow = base.priceLow;
        next.priceHigh = base.priceHigh;
      }
      rangeDraftRef.current = next;
      syncRangeBox();
      syncHandles();
    };
    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointermove', onMove);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointermove', onMove);
    };
  }, [payload, onPayloadChange, range, onRangePick, tool, onSelect]);

  const up = ohlc ? ohlc.close >= ohlc.open : true;
  const dig = ohlc ? priceDigits(ohlc.close) : digitsRef.current;

  const eraseIndBadge = (key: EraseIndKey, label: string, color: string, on: boolean) => {
    if (!on) return null;
    const hot = tool === 'eraser' && eraseIndHit === key;
    return (
      <button
        type="button"
        key={key}
        title={tool === 'eraser' ? t.indEraseChip : label}
        onClick={(e) => {
          e.stopPropagation();
          if (tool === 'eraser') disableIndicator(key);
        }}
        style={{
          pointerEvents: tool === 'eraser' ? 'auto' : 'none',
          border: hot ? '1px solid var(--short)' : '1px solid transparent',
          background: hot ? 'rgba(226,91,91,0.25)' : 'transparent',
          color, cursor: tool === 'eraser' ? 'pointer' : 'default',
          font: 'inherit', padding: '0 4px', borderRadius: 4,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div ref={wrapRef} style={{
      position: 'relative', width: '100%', height: '100%', minHeight: 360,
      background: 'var(--chart-bg)', overflow: 'hidden',
      // Let phone pan the desk; chart host also sets touch-action in createChart effect.
      touchAction: 'pan-x pan-y',
    }}>
      <div style={{
        position: 'absolute', zIndex: 5, top: 8, left: 10, right: 10,
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline',
        pointerEvents: 'none', fontFamily: 'var(--mono)', fontSize: 11,
        lineHeight: 1.35, maxHeight: '28%', overflow: 'hidden',
      }}>
        {ohlc ? (
          <>
            <span style={{ color: 'var(--muted)' }}>{ohlc.source === 'hover' ? 'bar' : 'last'}</span>
            <span style={{ color: up ? 'var(--long)' : 'var(--short)', whiteSpace: 'nowrap' }}>
              O {fmtPrice(ohlc.open, dig)} · H {fmtPrice(ohlc.high, dig)} · L {fmtPrice(ohlc.low, dig)} · C {fmtPrice(ohlc.close, dig)}
            </span>
            <span style={{ color: 'var(--muted)' }}>Vol {fmtVol(ohlc.volume)}</span>
            {live ? <span style={{ color: 'var(--gold)' }}>● live</span> : <span style={{ color: 'var(--muted)' }}>○ offline</span>}
            <span style={{ color: 'var(--muted)', opacity: 0.55 }}>#{tick}</span>
          </>
        ) : <span style={{ color: 'var(--muted)' }}>No data</span>}
        {eraseIndBadge('sma', 'SMA', '#c9a227', payload.indicators?.sma !== false)}
        {eraseIndBadge('ema', 'EMA', '#5b8def', Boolean(payload.indicators?.ema))}
        {eraseIndBadge('rsi', 'RSI', '#c084fc', payload.indicators?.rsi !== false)}
        {payload.indicators?.rsiZones ? <span style={{ color: '#c084fc', opacity: 0.75 }}>zones</span> : null}
        {eraseIndBadge('volume', 'Vol', 'var(--muted)', payload.indicators?.volume !== false)}
        {eraseIndBadge('autoLevels', 'levels', 'var(--gold)', Boolean(payload.indicators?.autoLevels))}
        {eraseIndBadge('autoTrends', 'trends', 'var(--muted)', Boolean(payload.indicators?.autoTrends))}
        {eraseIndBadge('orderBlocks', 'OB', '#5eead4', Boolean(payload.indicators?.orderBlocks))}
        {eraseIndBadge('obBreakers', 'BRK', '#94a3b8', Boolean(payload.indicators?.obBreakers))}
        {range ? <span style={{ color: 'var(--gold)' }}>range {fmtPrice(range.priceLow, dig)}–{fmtPrice(range.priceHigh, dig)}</span> : null}
        {backtestMarkers.length ? (
          <span style={{ color: '#6ec8ff' }}>
            sketch{strategyName ? ` · ${strategyName}` : ''} {backtestMarkers.length}
          </span>
        ) : null}
        {liveMarkers.length ? (
          <span style={{ color: '#5eead4' }} title="Real WEEX fills — stay after range clear">
            REAL {liveMarkers.length}
          </span>
        ) : null}
        {strategyStartTime ? (
          <span style={{ color: '#5b8def' }} title={t.strategyStartLine}>
            ▶ start
          </span>
        ) : null}
      </div>

      {strategyStartX != null ? (
        <div
          ref={strategyStartElRef}
          title={t.strategyStartLine}
          style={{
            position: 'absolute',
            left: strategyStartX,
            top: 28,
            bottom: 28,
            width: 0,
            borderLeft: '1px dashed rgba(91,141,239,0.85)',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 4,
            fontSize: 10,
            fontFamily: 'var(--mono)',
            color: '#5b8def',
            whiteSpace: 'nowrap',
            background: 'var(--overlay-chip)',
            padding: '1px 5px',
            borderRadius: 3,
          }}>
            ▶ start
          </div>
        </div>
      ) : null}

      {/* Pane crosshair stays subtle; DeskPage draws full-template crosshair when drawing. */}
      {cross && (tool === 'trend' || tool === 'range' || tool === 'hline' || tool === 'ruler' || tool === 'eraser') ? (
        <>
          <div style={{ position: 'absolute', left: cross.x, top: 0, bottom: 0, width: 1, background: 'rgba(232,237,245,0.22)', pointerEvents: 'none', zIndex: 4 }} />
          <div style={{ position: 'absolute', top: cross.y, left: 0, right: 0, height: 1, background: 'rgba(232,237,245,0.22)', pointerEvents: 'none', zIndex: 4 }} />
        </>
      ) : null}

      {/* Eraser capture square — shows what area a click will hit. */}
      {cross && tool === 'eraser' ? (
        <div
          title={eraseHit || eraseIndHit ? 'click to erase' : 'eraser area'}
          style={{
            position: 'absolute',
            left: cross.x - ERASER_HALF,
            top: cross.y - ERASER_HALF,
            width: ERASER_HALF * 2,
            height: ERASER_HALF * 2,
            border: `1.5px solid ${eraseHit || eraseIndHit ? '#e25b5b' : 'rgba(226,91,91,0.75)'}`,
            background: eraseHit || eraseIndHit ? 'rgba(226,91,91,0.22)' : 'rgba(226,91,91,0.08)',
            borderRadius: 3,
            pointerEvents: 'none',
            zIndex: 5,
            boxShadow: eraseHit || eraseIndHit ? '0 0 0 1px rgba(226,91,91,0.35)' : undefined,
          }}
        />
      ) : null}

      {ruler ? (() => {
        const chart = chartRef.current;
        const series = candleRef.current;
        if (!chart || !series) return null;
        const x1 = timeToX(ruler.t1);
        const x2 = timeToX(ruler.t2);
        const y1 = series.priceToCoordinate(ruler.p1);
        const y2 = series.priceToCoordinate(ruler.p2);
        if (x1 == null || x2 == null || y1 == null || y2 == null) return null;
        const ax = Number(x1);
        const ay = Number(y1);
        const bx = Number(x2);
        const by = Number(y2);
        const dPrice = ruler.p2 - ruler.p1;
        const dPct = ruler.p1 !== 0 ? (dPrice / Math.abs(ruler.p1)) * 100 : 0;
        const dSec = Math.abs(ruler.t2 - ruler.t1);
        const bars = timeframe
          ? Math.max(1, Math.round(dSec / Math.max(1, timeframeSeconds(timeframe))))
          : null;
        const fmtDur = (sec: number) => {
          if (sec < 60) return `${sec}s`;
          if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
          if (sec < 86400) {
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            return `${h}h ${m}m`;
          }
          const d = Math.floor(sec / 86400);
          const h = Math.floor((sec % 86400) / 3600);
          return `${d}d ${h}h`;
        };
        const midX = (ax + bx) / 2;
        const midY = (ay + by) / 2;
        const len = Math.hypot(bx - ax, by - ay) || 1;
        const angle = Math.atan2(by - ay, bx - ax) * (180 / Math.PI);
        const sign = dPrice > 0 ? '+' : '';
        const color = dPrice >= 0 ? '#3dba7a' : '#e25b5b';
        return (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 7 }}>
            <div style={{
              position: 'absolute',
              left: midX,
              top: midY,
              width: len,
              height: 0,
              borderTop: `1.5px dashed ${color}`,
              transform: `translate(-50%, -50%) rotate(${angle}deg)`,
              transformOrigin: 'center',
              opacity: 0.95,
            }}
            />
            <div style={{
              position: 'absolute', left: ax - 4, top: ay - 4, width: 8, height: 8,
              borderRadius: 8, background: color, boxShadow: '0 0 0 2px rgba(0,0,0,0.55)',
            }}
            />
            <div style={{
              position: 'absolute', left: bx - 4, top: by - 4, width: 8, height: 8,
              borderRadius: 8, background: color, boxShadow: '0 0 0 2px rgba(0,0,0,0.55)',
            }}
            />
            <div style={{
              position: 'absolute',
              left: Math.min(Math.max(8, midX + 10), Math.max(8, (wrapRef.current?.clientWidth || 320) - 160)),
              top: Math.min(Math.max(28, midY - 36), Math.max(28, (wrapRef.current?.clientHeight || 240) - 70)),
              background: 'rgba(10,14,20,0.92)',
              border: `1px solid ${color}`,
              borderRadius: 6,
              padding: '6px 8px',
              fontFamily: 'var(--mono)',
              fontSize: 11,
              color: 'var(--text)',
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
            }}
            >
              <div style={{ color, fontWeight: 700 }}>
                {sign}{fmtPrice(dPrice, digitsRef.current)} ({sign}{dPct.toFixed(2)}%)
              </div>
              <div style={{ color: 'var(--muted)' }}>
                {fmtDur(dSec)}{bars != null ? ` · ${bars} bar${bars === 1 ? '' : 's'}` : ''}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 10 }}>
                {fmtPrice(ruler.p1, digitsRef.current)} → {fmtPrice(ruler.p2, digitsRef.current)}
              </div>
            </div>
          </div>
        );
      })() : null}

      {avgEntryLevel && avgEntryY != null ? (
        <button
          ref={avgEntryElRef}
          type="button"
          title={avgEntryLevel.selected ? 'Position tools open' : 'Open position tools'}
          onClick={(e) => {
            e.stopPropagation();
            onAvgEntryClick?.();
          }}
          style={{
            position: 'absolute',
            right: 56,
            top: Math.max(28, avgEntryY - 12),
            zIndex: 6,
            padding: '3px 8px',
            borderRadius: 6,
            border: `1px solid ${avgEntryLevel.color || 'var(--gold)'}`,
            background: avgEntryLevel.selected
              ? 'rgba(0,0,0,0.85)'
              : 'rgba(0,0,0,0.72)',
            color: avgEntryLevel.color || 'var(--gold)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            fontWeight: 700,
            cursor: onAvgEntryClick ? 'pointer' : 'default',
            boxShadow: avgEntryLevel.selected
              ? `0 0 0 2px ${avgEntryLevel.color || 'var(--gold)'}55`
              : 'none',
          }}
        >
          {avgEntryLevel.title || 'POS'} · {fmtPrice(avgEntryLevel.price, dig)}
        </button>
      ) : null}

      {boxCss ? (
        <div
          ref={rangeBoxElRef}
          title={timeWarnTitle || 'Strategy range'}
          className={timeWarn.soon ? 'termix-range-time-warn' : undefined}
          style={{
            position: 'absolute',
            left: boxCss.left, top: boxCss.top, width: boxCss.width, height: boxCss.height,
            border: eraseHit === RANGE_DRAW_ID || payload.drawings.some((d) => d.id === eraseHit && d.type === 'range')
              ? '2px solid rgba(226,91,91,0.95)'
              : timeWarn.soon
                ? '2px solid rgba(226,91,91,0.95)'
                : selectedId === RANGE_DRAW_ID
                  ? '2px solid rgba(223,184,58,0.95)'
                  : '1px solid rgba(201,162,39,0.85)',
            background: eraseHit === RANGE_DRAW_ID || payload.drawings.some((d) => d.id === eraseHit && d.type === 'range')
              ? 'rgba(226,91,91,0.22)'
              : timeWarn.soon
                ? 'rgba(226,91,91,0.14)'
                : 'rgba(201,162,39,0.08)',
            boxShadow: timeWarn.soon
              ? '0 0 0 2px rgba(226,91,91,0.35)'
              : selectedId === RANGE_DRAW_ID ? '0 0 0 3px rgba(223,184,58,0.25)' : 'none',
            pointerEvents: 'none', zIndex: 3, boxSizing: 'border-box',
          }}
        >
          {timeWarn.soon ? (
            <>
              {/* Flashing right time edge */}
              <div
                title={timeWarnTitle}
                style={{
                  position: 'absolute', top: -2, bottom: -2, right: -2, width: 4,
                  background: 'var(--short)', borderRadius: 2,
                  animation: 'termix-live-blink 0.9s ease-in-out infinite',
                }}
              />
              <div style={{
                position: 'absolute', left: 6, top: 4, zIndex: 3,
                maxWidth: '90%', padding: '3px 7px', borderRadius: 6,
                background: 'rgba(226,91,91,0.92)', color: '#fff',
                fontSize: 10, fontFamily: 'var(--mono)', lineHeight: 1.3,
                pointerEvents: 'none',
              }}>
                {t.rangeTimeEndingLeft(fmtCountdown(timeWarn.leftSec))}
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {obBoxes.map((b, i) => {
        const bull = b.side === 'bull';
        const breaker = b.kind === 'breaker';
        const mitigated = breaker || b.status === 'mitigated';
        const strength = Math.max(0, Math.min(100, b.strength || 50));
        const alpha = breaker ? 0.42 : b.status === 'tested' ? 0.82 : 0.95;
        const wash = breaker ? 0.04 : 0.10 + (strength / 100) * 0.16;
        const edge = bull
          ? (breaker ? 'rgba(61,186,122,0.35)' : `rgba(61,186,122,${0.62 + strength / 280})`)
          : (breaker ? 'rgba(226,91,91,0.35)' : `rgba(226,91,91,${0.62 + strength / 280})`);
        const fill = bull
          ? `rgba(61,186,122,${wash})`
          : `rgba(226,91,91,${wash})`;
        const buyPct = b.buyPct ?? 50;
        const sellPct = b.sellPct ?? 50;
        const showActivity = !breaker && b.height >= 12 && b.width >= 40;
        const metricColor = breaker
          ? 'rgba(170,178,188,0.92)'
          : (bull ? 'rgba(120,230,175,0.98)' : 'rgba(255,150,150,0.98)');
        const eraseKey: EraseIndKey = breaker ? 'obBreakers' : 'orderBlocks';
        return (
          <div key={`ob-${b.kind}-${i}`}>
            <div
              ref={(el) => {
                const key = `${b.kind}-${i}`;
                const cur = obDomRef.current.get(key) || { box: null as unknown as HTMLDivElement, metric: null };
                if (el) cur.box = el;
                obDomRef.current.set(key, cur);
              }}
              title={b.title}
              style={{
                position: 'absolute',
                left: b.left, top: b.top, width: b.width, height: b.height,
                border: `${breaker ? '1px dashed' : '1px solid'} ${edge}`,
                borderLeft: breaker
                  ? undefined
                  : `2px solid ${bull ? 'rgba(61,186,122,0.95)' : 'rgba(226,91,91,0.95)'}`,
                background: fill,
                opacity: alpha,
                pointerEvents: tool === 'eraser' ? 'auto' : 'none',
                cursor: tool === 'eraser' ? 'pointer' : 'default',
                zIndex: breaker ? 3 : 4, boxSizing: 'border-box',
                outline: eraseIndHit === eraseKey ? '1px solid var(--short)' : undefined,
                overflow: 'hidden',
              }}
              onClick={(e) => {
                if (tool !== 'eraser') return;
                e.stopPropagation();
                disableIndicator(eraseKey);
              }}
            >
              {showActivity ? (
                <div style={{
                  position: 'absolute', left: 3, top: 2, bottom: 2,
                  width: Math.min(28, Math.max(14, b.width * 0.14)),
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2,
                  pointerEvents: 'none',
                }}>
                  <div style={{ height: 3, borderRadius: 1, background: 'rgba(0,0,0,0.28)', overflow: 'hidden' }}>
                    <div style={{ width: `${buyPct}%`, height: '100%', background: 'rgba(61,186,122,0.9)' }} />
                  </div>
                  <div style={{ height: 3, borderRadius: 1, background: 'rgba(0,0,0,0.28)', overflow: 'hidden' }}>
                    <div style={{ width: `${sellPct}%`, height: '100%', background: 'rgba(226,91,91,0.9)' }} />
                  </div>
                </div>
              ) : null}
              {b.midY != null && b.midY >= b.top + 2 && b.midY <= b.top + b.height - 2 ? (
                <div
                  data-ob-mid
                  style={{
                    position: 'absolute', left: 0, right: 0, top: b.midY - b.top,
                    borderTop: `1px dashed ${bull ? 'rgba(61,186,122,0.5)' : 'rgba(226,91,91,0.5)'}`,
                    pointerEvents: 'none',
                  }}
                />
              ) : null}
            </div>
            {b.metric ? (
              <div
                ref={(el) => {
                  const key = `${b.kind}-${i}`;
                  const cur = obDomRef.current.get(key) || { box: null as unknown as HTMLDivElement, metric: null };
                  cur.metric = el;
                  obDomRef.current.set(key, cur);
                }}
                title={b.title}
                style={{
                  position: 'absolute',
                  left: Math.max(4, b.left + b.width + 4),
                  top: b.metricTop,
                  zIndex: 5,
                  pointerEvents: 'none',
                  fontSize: 10,
                  fontFamily: 'var(--mono)',
                  fontWeight: 700,
                  letterSpacing: 0.02,
                  color: metricColor,
                  textShadow: '0 0 4px var(--bg), 0 1px 2px rgba(0,0,0,0.85)',
                  whiteSpace: 'nowrap',
                }}
              >
                {b.metric}
              </div>
            ) : null}
          </div>
        );
      })}

      {/* TradingView-style drag chips: drop on upper half → main chart, lower → separate pane */}
      {paneChips.map((chip) => (
        <div
          key={`chip-${chip.key}`}
          title={t.indPaneDragHint}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const wrap = wrapRef.current;
            if (!wrap) return;
            const rect = wrap.getBoundingClientRect();
            setDragChip({ key: chip.key, x: e.clientX - rect.left, y: e.clientY - rect.top });
            const onMove = (ev: PointerEvent) => {
              setDragChip({ key: chip.key, x: ev.clientX - rect.left, y: ev.clientY - rect.top });
            };
            const onUp = (ev: PointerEvent) => {
              window.removeEventListener('pointermove', onMove);
              window.removeEventListener('pointerup', onUp);
              const y = ev.clientY - rect.top;
              const h = rect.height || 1;
              const toMain = y < h * 0.55;
              const ind = payloadRef.current.indicators || {};
              if (chip.key === 'rsi') {
                onPayloadChange({
                  ...payloadRef.current,
                  indicators: { ...ind, rsi: true, rsiPane: toMain ? 'main' : 'separate' },
                });
              } else {
                onPayloadChange({
                  ...payloadRef.current,
                  indicators: { ...ind, volume: true, volumePane: toMain ? 'main' : 'separate' },
                });
              }
              setDragChip(null);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
          }}
          style={{
            position: 'absolute', left: chip.left, top: chip.top, zIndex: 10,
            padding: '3px 8px', borderRadius: 6, cursor: 'grab', userSelect: 'none',
            border: `1px solid ${chip.key === 'rsi' ? '#c084fc' : 'var(--line)'}`,
            background: 'var(--panel)', color: chip.key === 'rsi' ? '#c084fc' : 'var(--text)',
            fontSize: 11, fontFamily: 'var(--mono)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          ⠿ {chip.label}
          <span style={{ marginLeft: 6, opacity: 0.55, fontSize: 9 }}>
            {chip.mode === 'main' ? 'main' : 'pane'}
          </span>
        </div>
      ))}
      {dragChip ? (
        <div style={{
          position: 'absolute', left: dragChip.x + 8, top: dragChip.y + 8, zIndex: 20,
          padding: '4px 10px', borderRadius: 6, pointerEvents: 'none',
          border: '1px solid var(--gold)', background: 'var(--panel)', color: 'var(--gold)',
          fontSize: 11, fontFamily: 'var(--mono)',
        }}>
          {dragChip.key === 'rsi' ? 'RSI' : 'Vol'} → {dragChip.y < (wrapRef.current?.clientHeight || 400) * 0.55 ? 'main' : 'pane'}
        </div>
      ) : null}

      {historyLoading ? (
        <div
          style={{
            position: 'absolute', left: 10, bottom: 36, zIndex: 8,
            padding: '4px 10px', borderRadius: 8,
            border: '1px solid var(--line)', background: 'var(--overlay-chip)',
            color: 'var(--gold)', fontSize: 11, fontFamily: 'var(--mono)',
            pointerEvents: 'none',
          }}
        >
          {t.indHistoryLoading}
        </div>
      ) : null}

      {payload.indicators?.barCountdown !== false && barLeftSec != null && priceLabelY != null && ohlc ? (
        <div
          ref={barCdElRef}
          title="Time to bar close · live heartbeat"
          style={{
            position: 'absolute',
            // LWC last-price pill is ~18px tall, centered on priceY — sit flush under it
            right: 0,
            top: priceLabelY + 11,
            zIndex: 9,
            pointerEvents: 'none',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: 0.02,
            padding: '2px 7px',
            borderRadius: 2,
            background: up ? 'var(--long)' : 'var(--short)',
            color: '#fff',
            fontWeight: 700,
            lineHeight: 1.2,
            boxShadow: '0 1px 2px rgba(0,0,0,0.45)',
            // No CSS transition — Safari composites lag behind pan when top animates.
          }}
        >
          {barLeftSec < 0 ? '0:00' : fmtCountdown(barLeftSec)}
        </div>
      ) : null}

      {showJumpLive ? (
        <button
          type="button"
          title={t.jumpToLiveTitle}
          aria-label={t.jumpToLive}
          onClick={(e) => {
            e.stopPropagation();
            const chart = chartRef.current;
            if (!chart) return;
            pinRightRef.current = true;
            userInteractedRef.current = false;
            setShowJumpLive(false);
            scrollChartToLatest(chart, normalizeCandles(candlesRef.current), hostWidth());
            requestAnimationFrame(() => {
              if (chartRef.current) {
                scrollChartToLatest(
                  chartRef.current,
                  normalizeCandles(candlesRef.current),
                  hostWidth(),
                );
              }
            });
          }}
          style={{
            position: 'absolute',
            right: 58,
            bottom: 28,
            zIndex: 8,
            width: 28,
            height: 28,
            padding: 0,
            borderRadius: 6,
            border: '1px solid var(--line)',
            background: 'var(--overlay-chip)',
            color: 'var(--text)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            transition: 'background 120ms ease, border-color 120ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--gold)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--line)';
          }}
        >
          <svg width="16" height="14" viewBox="0 0 16 14" aria-hidden="true">
            {/* TradingView-style jump-to-realtime chevrons */}
            <path d="M1.5 2.5 L6.5 7 L1.5 11.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.5 2.5 L12.5 7 L7.5 11.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14.2 2.5 v9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      ) : null}

      {tool === 'eraser' && eraseHit ? (
        <div style={{
          position: 'absolute', zIndex: 8, top: 36, left: 10,
          padding: '4px 8px', borderRadius: 6, fontSize: 11, fontFamily: 'var(--mono)',
          background: 'rgba(226,91,91,0.92)', color: '#fff', pointerEvents: 'none',
        }}>
          delete: {payload.drawings.find((d) => d.id === eraseHit)?.type || 'object'}
        </div>
      ) : null}

      {draftPt ? (
        <div style={{
          position: 'absolute', left: draftPt.x - 5, top: draftPt.y - 5, width: 10, height: 10,
          borderRadius: '50%', border: `2px solid ${drawColor}`, background: 'var(--overlay-chip)',
          pointerEvents: 'none', zIndex: 6,
        }} title="Start point" />
      ) : null}

      {handles.map((h) => (
        <div
          key={`${h.id}-${h.end}`}
          ref={(el) => {
            const key = `${h.id}:${h.end}`;
            if (el) handlesElRef.current.set(key, el);
            else handlesElRef.current.delete(key);
          }}
          title={
            h.kind === 'range' ? 'Drag range edge'
              : h.kind === 'hline' ? 'Drag horizontal line'
                : 'Drag trend point'
          }
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const origin = h.kind === 'range' ? (rangeRef.current || undefined) : undefined;
            if (h.kind === 'range' && rangeRef.current) {
              rangeDraftRef.current = { ...rangeRef.current };
            }
            dragRef.current = { kind: h.kind, id: h.id, end: h.end, origin };
            onSelect?.(h.id);
          }}
          style={{
            position: 'absolute',
            left: h.kind === 'range' && (h.end === 't' || h.end === 'b') ? h.x - 14
              : h.kind === 'range' && (h.end === 'l' || h.end === 'r') ? h.x - 4
                : h.x - 6,
            top: h.kind === 'range' && (h.end === 'l' || h.end === 'r') ? h.y - 14
              : h.kind === 'range' && (h.end === 't' || h.end === 'b') ? h.y - 4
                : h.y - 6,
            width: h.kind === 'range' && (h.end === 't' || h.end === 'b') ? 28
              : h.kind === 'range' && (h.end === 'l' || h.end === 'r') ? 8
                : 12,
            height: h.kind === 'range' && (h.end === 'l' || h.end === 'r') ? 28
              : h.kind === 'range' && (h.end === 't' || h.end === 'b') ? 8
                : 12,
            borderRadius: h.kind === 'range' || h.kind === 'hline' ? 3 : '50%',
            border: `2px solid ${eraseHit === h.id ? '#e25b5b' : selectedId === h.id ? '#dfb83a' : h.color}`,
            background: eraseHit === h.id ? 'rgba(226,91,91,0.5)' : 'var(--overlay-chip)',
            cursor: h.kind === 'trend' ? 'grab'
              : h.end === 'l' || h.end === 'r' ? 'ew-resize'
                : h.end === 'tl' || h.end === 'br' ? 'nwse-resize'
                  : h.end === 'tr' || h.end === 'bl' ? 'nesw-resize'
                    : 'ns-resize',
            zIndex: 7,
            boxShadow: eraseHit === h.id
              ? '0 0 0 4px rgba(226,91,91,0.25)'
              : selectedId === h.id ? '0 0 0 3px rgba(223,184,58,0.3)' : 'none',
          }}
        />
      ))}

      <div ref={hostRef} style={{ width: '100%', height: '100%', minHeight: 360 }} />
    </div>
  );
}
